import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button, Pagination, notification, Table as AntTable, Spin, Checkbox, Modal } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { ColumnsType } from 'antd/es/table';
import {
    XIcon,
    CreditCardIcon,
    Plus,
    FileText,
    FileArrowDown,
    SealCheck,
    SquaresFour,
    Table,
    CaretDown,
    Eye,
    Sun,
    BatteryCharging,
    Calculator,
    Cloud,
    FolderOpen,
    Cube,
    Article,
} from '@phosphor-icons/react';
import { useViewerStore } from '../../../../store/store';
import {
    useOneButtonStore,
    COMMON_CONSTANTS,
    type FinancingProduct,
    type PriceCell,
    type PartnerSyncChecklist,
    type DashboardQuotesSummaryLines,
} from '../../../../store/oneButtonStore';
import quoteServices, {
    type DealOpportunityProductAdder,
    type RetrievePriceQuoteResponse,
} from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import OBDQuoteDetailsModal from './OBDQuoteDetailsModal';
import OBDAddQuoteModal, { type OBDAddQuoteFormState } from './OBDAddQuoteModal';
import { isPartnerSyncCompleteForConfig, isLightreachEligibleProductsPrerequisiteMet } from './partnerSyncConfig';
import GenerateQuoteModal, { QuoteFormState } from './GenerateQuoteModal';
import RoofingCalculatorModal, { shouldShowRoofSolarCalculatorMenu } from './RoofingCalculatorModal';
import AddersModal, { type AddersModalHandle } from './AddersModal';
import IGSCapsModal from './IGSCapsModal';
import IGSEpcCalcModal from './IGSEpcCalcModal';
import { selectedQuote } from './FinancingGridCard';
import { OBD_QUOTES_CONTRACT_CONFIRM_Z } from './oneButtonOverlayZ';
import DashboardModal from './DashboardModal';
import LightreachBatteryCapModal from './LightreachBatteryCapModal';
import SunnovaSystemErrorModal, { type SunnovaSystemErrorPayload } from './SunnovaSystemErrorModal';
import {
    getOneButtonCapabilityValue,
    oneButtonCapabilityRawAllows,
} from '../../../../services/oneButtonServices/capabilityService';
import { buildSelectedQuoteForGenerateFromGridCell } from './buildGenerateQuoteFromFinanceLine';
import designServices from '../../../../services/oneButtonServices/designServices';
import { ViewTrinityContractModal } from './ViewTrinityContractModal';
import type { ViewTrinityContractRow } from './ViewTrinityContractModal';
import { useSunnyFocusedSectionOpen } from './useSunnyFocusedSectionOpen';

/** Quotes card grid: max items per page (Commerce Hub + dashboard section). */
const QUOTES_PAGE_SIZE = 4;

const QUOTE_PRODUCT_TAB_ORDER: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Battery',
    'Solar + Roof',
    'Solar + Battery',
    'Battery + Roof',
];

function getAvailableAdderProductTypesFromOpportunity(
    opportunityDetails: any | null | undefined,
    productType: string | null | undefined,
): string[] {
    const productTypes: string[] = [];
    if (productType === 'RR') return ['R&R'];

    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};
    if (interest?.RRR) productTypes.push('RRR');
    if (interest?.Solar) productTypes.push('Solar');
    if (interest?.Battery) productTypes.push('Battery');
    if (interest?.Roof) productTypes.push('Roof');
    return productTypes;
}

type ContractStatus = 'Submitted' | 'Cancelled';

interface FinanceItem {
    key: string;
    partner: string;
    product: string;
    isCash: boolean;
    monthly: number;
    apr: number;
    term: number;
}

interface QuoteRow {
    id: string;
    sourceKey: string;
    partner: string;
    product: string;
    pricingMethod: string;
    solarRate: number;
    solarEscalator: number;
    grossPpw: number;
    downPayment: number;
    term: number;
    apr: number;
    monthly: number;
    hasContract?: boolean;
    applicationId?: string;
    applicationStatus?: ContractStatus;
    /** Trinity / Angular `isMovedToContract` — quote already submitted to contract flow */
    isMovedToContract?: boolean;
    systemSizeKw?: number;
    productionKwh?: number;
    /** Quote expiration date (e.g. ISO or MM/DD/YYYY); optional from API */
    quoteExpiresAt?: string | null;
    quoteCreatedStr?: string | null;
    epc:number | null;
    /**IGS Error details */
    SunnovaQuote:string,
    ErrorMessage:any,
    IsQuote_Change_Order: boolean;
    IsQuote_Used: boolean;
    isSelectedCO: boolean;
    /** GetQuotes / contract row finance method (design-loss check for GoodLeap change order). */
    financeMethodName?: string;
    Finance_Partner_Contract_Status?: string;
    clonedQuoteId?: number;
    Price_Per_Sqft?: number;
    Roof_Square_Override?: number;
    fnanceMethod?:string
}

/** Normalize API boolean-ish fields (camelCase or PascalCase). */
function truthyApiFlag(v: unknown): boolean {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
}

/**
 * Angular `quote-vesrion.component.ts` builds each row with `isMovedToContract: ele.isMovedToContract`.
 * `getData` also merges `IsMove_To_Contract` from `quoteVersion` onto `ele` (Trinity field name variant).
 * Child templates (e.g. `solar-quote.component.html`) show read-only "Moved to contract" for **Good Leap** when
 * `quoteInfo.FinancePartner == goodLeapPartnerName && quoteInfo.isMovedToContract`, and use
 * `quotesAddedToContracts` is filled from `ele.Selected` in `getData()` — same as **`Selected: true`** on
 * `QuoteVersion` rows in RetrievePriceQuote (often still `isMovedToContract: false` until Trinity syncs).
 * Light Reach disables the checkbox when `Finance_Partner_Contract_Status` is set — we treat that as moved for the green icon.
 */
/** Angular session list: quote is checked for “move to contract” / contract pack (checkbox hidden when in list). */
function quoteVersionSelectedForContractPack(row: Record<string, unknown>): boolean {
    return truthyApiFlag(row.Selected ?? row.selected);
}

function angularApiMovedToContract(q: Record<string, unknown>): boolean {
    return truthyApiFlag(
        q.isMovedToContract ??
            q.IsMovedToContract ??
            q.IsMove_To_Contract ??
            q.isMoveToContract ??
            q.IsMoveToContract ??
            q.is_moved_to_contract ??
            q.is_move_to_contract,
    );
}

function normalizeFinancePartnerKey(partner: string): string {
    return (partner || '').replace(/\s/g, '').toLowerCase();
}

//LightReach Eligible Product Toolbar Visible
function lightReachEligibleProductsToolbarVisible(od: any): boolean {
    return (
        !!od?.OpportunityInterest?.Solar &&
        (od?.OpportunityProduct || []).find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag)
            ?.Finance_Partner_Display_Name === 'Lightreach'
    );
}

function isLightreachFinancePartner(partner: string): boolean {
    return (
        normalizeFinancePartnerKey(partner) ===
        normalizeFinancePartnerKey(COMMON_CONSTANTS.LightreachPartnerName)
    );
}

function isGoodLeapFinancePartner(partner: string): boolean {
    return (
        normalizeFinancePartnerKey(partner) ===
        normalizeFinancePartnerKey(COMMON_CONSTANTS.GoodLeapPartnerName)
    );
}

/** Good Leap quotes list: show “Change Order” when API marks the row for change order (Trinity `isSelectedCO` / `IsQuote_Change_Order`). */
function goodLeapQuoteChangeOrderUiEligible(q: QuoteRow): boolean {
    return (
        isGoodLeapFinancePartner(q.partner) &&
        !!q.id && (q.IsQuote_Change_Order === true || q.IsQuote_Used === true)
    );
}

/** Angular Lightreach: moved pipeline when contract id/status exists on quote row. */
function contractStatusPillClass(status: string): string {
    const normalized = status.trim().toLowerCase();
    const base = 'obd-quote-card__contract-status';
    if (normalized === 'voided' || normalized === 'void') {
        return `${base} obd-quote-card__contract-status--voided`;
    }
    if (normalized === 'signed' || normalized === 'complete' || normalized === 'completed') {
        return `${base} obd-quote-card__contract-status--signed`;
    }
    if (normalized === 'sent' || normalized === 'generated' || normalized === 'pending') {
        return `${base} obd-quote-card__contract-status--pending`;
    }
    if (normalized === 'active' || normalized === 'in progress' || normalized === 'in_progress') {
        return `${base} obd-quote-card__contract-status--active`;
    }
    return `${base} obd-quote-card__contract-status--neutral`;
}

function inferLightreachPartnerContractPipeline(q: Record<string, unknown>): boolean {
    const fid = q.Finance_Partner_Contract_Id ?? q.finance_Partner_Contract_Id ?? q.financePartnerContractId;
    if (fid != null && String(fid).trim() !== '') return true;
    const fps = q.Finance_Partner_Contract_Status ?? q.finance_Partner_Contract_Status ?? q.financePartnerContractStatus;
    if (fps == null) return false;
    const s = String(fps).trim();
    if (s === '' || s.toLowerCase() === 'null') return false;
    return true;
}

/** Green contract icon: Angular server/session moved state on `QuoteRow` after mapping + merge. */
function isQuoteMovedToContract(q: QuoteRow): boolean {
    if (q.isMovedToContract) return true;
    return !!(q.hasContract && q.applicationId);
}

/** RetrievePriceQuote `QuoteVersion` row — same flags as Angular `modifiedData` / quoteVersion merge. */
function inferMovedFromQuoteVersionRow(row: Record<string, unknown>): boolean {
    if (quoteVersionSelectedForContractPack(row)) return true;
    if (angularApiMovedToContract(row)) return true;
    if (truthyApiFlag(row.hasContract ?? row.HasContract)) return true;
    const fp = String(row.FinancePartner ?? row.financePartner ?? '');
    if (isLightreachFinancePartner(fp) && inferLightreachPartnerContractPipeline(row)) return true;
    return false;
}

function movedFromRetrievePriceQuoteCache(quoteId: string, cache: RetrievePriceQuoteResponse | null | undefined): boolean {
    const raw = cache?.QuoteVersion;
    if (!Array.isArray(raw)) return false;
    const n = Number(quoteId);
    if (!Number.isFinite(n)) return false;
    for (const row of raw) {
        if (!row || typeof row !== 'object') continue;
        const r = row as Record<string, unknown>;
        const rid = Number(r.QuoteId ?? r.quoteId);
        if (rid === n) return inferMovedFromQuoteVersionRow(r);
    }
    return false;
}

/** Merge GetQuotes rows with prior UI state so a follow-up refresh does not clear “moved” after SubmitVersion. */
function mergeQuotesAfterFetch(
    mapped: QuoteRow[],
    prev: QuoteRow[],
    localMovedIds: ReadonlySet<string>,
    retrieveCache: RetrievePriceQuoteResponse | null | undefined,
): QuoteRow[] {
    return mapped.map((m) => {
        let row = { ...m };
        if (localMovedIds.has(m.id)) {
            row = {
                ...row,
                hasContract: true,
                isMovedToContract: true,
                applicationId: row.applicationId ?? `APP-${m.id}`,
                applicationStatus: row.applicationStatus ?? 'Submitted',
            };
        }
        if (movedFromRetrievePriceQuoteCache(m.id, retrieveCache)) {
            row = {
                ...row,
                hasContract: true,
                isMovedToContract: true,
                applicationId: row.applicationId ?? `APP-${m.id}`,
                applicationStatus: row.applicationStatus ?? 'Submitted',
            };
        }
        const old = prev.find((p) => p.id === m.id);
        if (old && isQuoteMovedToContract(old)) {
            row = {
                ...row,
                hasContract: true,
                isMovedToContract: true,
                applicationId: old.applicationId ?? row.applicationId ?? `APP-${m.id}`,
                applicationStatus: old.applicationStatus ?? row.applicationStatus ?? 'Submitted',
            };
        }
        return row;
    });
}

interface InitialContractState {
    applicationId: string;
    status: ContractStatus;
}

function buildDashboardQuotesSummaryLines(
    savedQuotes: QuoteRow[],
    initialContracts: Record<string, InitialContractState>,
    items: FinanceItem[],
): DashboardQuotesSummaryLines {
    let selectedQuoteLine: string | null = null;
    if (savedQuotes.length > 0) {
        const q = savedQuotes[savedQuotes.length - 1];
        const cash = q.pricingMethod === 'Cash';
        const mo = cash
            ? `$${Math.round(q.monthly).toLocaleString()}`
            : `$${q.monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
        selectedQuoteLine = `${q.partner} · ${q.product} · ${mo} · #${q.id}`;
    }
    let signedContractLine: string | null = null;
    const contracted = savedQuotes.filter((q) => isQuoteMovedToContract(q));
    if (contracted.length > 0) {
        const c = contracted[contracted.length - 1];
        signedContractLine = `${c.partner} · ${c.product} · ${c.applicationId ?? 'Moved to contract'} · ${c.applicationStatus ?? 'Submitted'}`;
    } else {
        for (const item of items) {
            const ic = initialContracts[item.key];
            if (ic && ic.status === 'Submitted' && ic.applicationId) {
                signedContractLine = `${item.partner} · ${item.product} · ${ic.applicationId} · ${ic.status}`;
                break;
            }
        }
    }
    return { selectedQuoteLine, signedContractLine };
}

function calcMonthly(principal: number, aprPct: number, termYears: number): number {
    if (termYears <= 0 || principal <= 0) return 0;
    const r = aprPct / 100 / 12;
    const n = termYears * 12;
    if (r === 0) return Math.round(principal / n);
    return Math.round((principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
}

function formatPartnerDisplayName(partner: string): string {
    const p = (partner || '').trim();
    if (p.toLowerCase() === 'goodleap') return 'Good Leap';
    if (p.toLowerCase() === 'lightreach') return 'Light Reach';
    if (p.toLowerCase() === 'igs') return 'IGS';
    if (p.toLowerCase() === 'cash') return 'Cash';
    return p;
}

/** Finance partner label from an OpportunityProduct row (Commerce Hub / Products section). */
function partnerDisplayNameFromOpportunityProduct(p: Record<string, unknown> | null | undefined): string {
    if (!p) return '';
    return String(p.Finance_Partner_Display_Name ?? p.Finance_Partner ?? p.financePartnerName ?? '').trim();
}

/** Finance method label from an OpportunityProduct row. */
function financeMethodNameFromOpportunityProduct(p: Record<string, unknown> | null | undefined): string {
    if (!p) return '';
    return String(
        p.Finance_Method_Name ?? p.finance_Method_Name ?? p.Finance_Method ?? p.Contract_Type ?? p.contract_Type ?? '',
    ).trim();
}

/** Good Leap or Lightreach solar financing — Battery add-quote selection is allowed only in this case. */
function isGoodLeapOrLightreachSolarFinancing(partnerDisplayName: string): boolean {
    const k = partnerDisplayName.trim().toLowerCase().replace(/\s+/g, '');
    if (!k) return false;
    const gl = COMMON_CONSTANTS.GoodLeapPartnerName.toLowerCase().replace(/\s+/g, '');
    const lr = COMMON_CONSTANTS.LightreachPartnerName.toLowerCase().replace(/\s+/g, '');
    return k === gl || k.includes('goodleap') || k === lr || k.includes('lightreach');
}

function normalizePartnerKeyForAddQuoteCompare(name: string): string {
    const k = name.trim().toLowerCase().replace(/\s+/g, '');
    if (!k) return '';
    if (k.includes('good') && k.includes('leap')) return 'goodleap';
    if (k.includes('light') && k.includes('reach')) return 'lightreach';
    if (k === 'igs' || k.startsWith('igs')) return 'igs';
    if (k.includes('cash')) return 'cash';
    if (k.includes('sunnova')) return 'sunnova';
    if (k.includes('sunlight')) return 'sunlight';
    return k;
}

/** True when active Solar and Battery rows use the same finance partner (Commerce Hub / opportunity). */
function solarAndBatteryShareFinancePartner(
    solar: Record<string, unknown> | null | undefined,
    battery: Record<string, unknown> | null | undefined,
): boolean {
    if (!solar || !battery) return false;
    const sp = normalizePartnerKeyForAddQuoteCompare(partnerDisplayNameFromOpportunityProduct(solar));
    const bp = normalizePartnerKeyForAddQuoteCompare(partnerDisplayNameFromOpportunityProduct(battery));
    if (!sp || !bp) return false;
    return sp === bp;
}

/** Trinity generate-quote `checkGoodLeapMultiplier` — Good Leap + Solar + active product. */
function findGoodLeapSolarFinancePartnerId(opp: Record<string, unknown> | null | undefined): string | number | null {
    if (!opp) return null;
    const products = (opp.OpportunityProduct ?? opp.opportunityProduct) as unknown[] | undefined;
    if (!Array.isArray(products)) return null;
    const gl = COMMON_CONSTANTS.GoodLeapPartnerName;
    const solar = COMMON_CONSTANTS.SolarProductName;
    for (const raw of products) {
        if (!raw || typeof raw !== 'object') continue;
        const p = raw as Record<string, unknown>;
        const fp = String(p.Finance_Partner ?? p.Finance_Partner_Display_Name ?? '').trim();
        if (fp !== gl) continue;
        if (!p.ActiveFlag) continue;
        const ptn = String(p.Product_Type_Name ?? p.ProductTypeName ?? '').trim();
        if (ptn !== solar) continue;
        const id = p.Finance_Partner_Id ?? p.financePartnerId;
        if (id != null && String(id).trim() !== '') return id as string | number;
    }
    return null;
}

/** Trinity `generate-quote.component.ts` submitVersion clears null ErrorMessage before POST. */
function normalizeQuoteVersionErrorMessages(rows: Record<string, unknown>[]) {
    rows.forEach((row) => {
        const em = row.ErrorMessage ?? row.errorMessage;
        if (em == null) row.ErrorMessage = '';
    });
}

function firstTpoValidationRow(res: unknown): {
    Validation_Status?: boolean;
    Action_Type?: string;
    Popup_Message?: string;
} | null {
    if (res == null || typeof res !== 'object') return null;
    const o = res as Record<string, unknown>;
    const arr = (o.data ?? o.Data) as unknown;
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const row = arr[0];
    return row && typeof row === 'object' ? (row as { Validation_Status?: boolean; Action_Type?: string; Popup_Message?: string }) : null;
}

/** Format quote expiry for display: "MM/DD/YYYY (N Days)" or null */
function formatQuoteExpiry(expiresAt: string | null | undefined): string | null {
    if (!expiresAt) return null;
    const d = new Date(expiresAt);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const days = Math.max(0, Math.ceil((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy} (${days} Days)`;
}

function formToMonthly(f: OBDAddQuoteFormState, systemWatts: number): number {
    const ppw = parseFloat(f.grossPpw) || 0;
    const down = parseFloat(f.downPayment) || 0;
    const apr = parseFloat(f.apr) || 0;
    const term = parseInt(f.term) || 25;
    const principal = ppw * systemWatts - down;
    if (f.pricingMethod === 'Cash') return Math.round(principal + down);
    return calcMonthly(principal > 0 ? principal : 0, apr, term);
}

let _quoteIdCounter = 0;
function nextQuoteId() { return `obd-q-${++_quoteIdCounter}`; }

let _appIdCounter = 264012456;
function nextApplicationId() {
    _appIdCounter += 1;
    const s = String(_appIdCounter).padStart(8, '0');
    return `26-40-${s.slice(-5)}`;
}

function isPartnerSyncComplete(partnerName: string, status: PartnerSyncChecklist | undefined): boolean {
    return isPartnerSyncCompleteForConfig(partnerName, status);
}

function getLightreachLeadIdFromOpportunity(od: any): string | null {
    if (!od) return null;
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        [];
    if (Array.isArray(list)) {
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (!fp.includes('lightreach')) continue;
            const pt = String(row.ProductTypeName ?? row.Product_Type_Name ?? '').toLowerCase();
            if (pt.includes('solar')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (fp.includes('lightreach')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
    }
    const top = od.Finance_Partner_Sync_LeadID ?? od.OpportunityDetails?.Finance_Partner_Sync_LeadID;
    return top != null && String(top).trim() !== '' ? String(top) : null;
}

function normalizeLightreachPricingRows(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) return raw as Record<string, unknown>[];
    if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        if (Array.isArray(o.Data)) return o.Data as Record<string, unknown>[];
        if (Array.isArray(o.eligibleProductDetails)) return o.eligibleProductDetails as Record<string, unknown>[];
    }
    return [];
}

function sortLightreachEligibleRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return [...rows].sort((a, b) => {
        const escA = Number(a.EscalationRate);
        const escB = Number(b.EscalationRate);
        const preferA = escA === 0.0299 ? 0 : 1;
        const preferB = escB === 0.0299 ? 0 : 1;
        if (preferA !== preferB) return preferA - preferB;
        const kwhA = Number(a.KwhRate) || 0;
        const kwhB = Number(b.KwhRate) || 0;
        if (kwhA !== kwhB) return kwhA - kwhB;
        return (Number(a.EscalationRate) || 0) - (Number(b.EscalationRate) || 0);
    });
}

function fmtUsdCell(n: unknown): string {
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(x);
}

function fmtUsdCellPreservePrecision(n: unknown): string {
    if (n == null || n === '') return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return String(n);
    const raw = String(n).trim();
    const fracLen = raw.includes('.') ? raw.split('.')[1]?.length ?? 0 : 0;
    const maxFractionDigits = Math.min(Math.max(fracLen, 2), 10);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: maxFractionDigits,
    }).format(x);
}

function fmtEscalationCell(v: unknown): string {
    if (v == null || v === '') return '—';
    const x = Number(v);
    if (Number.isNaN(x)) return String(v);
    if (x > 0 && x <= 1) return `${(x * 100).toFixed(2)}%`;
    return `${x}%`;
}

function lrSortTextField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) =>
        String(a[field] ?? '').localeCompare(String(b[field] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function lrSortNumericField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) => {
        const na = Number(a[field]);
        const nb = Number(b[field]);
        const va = Number.isFinite(na) ? na : 0;
        const vb = Number.isFinite(nb) ? nb : 0;
        return va - vb;
    };
}

const LIGHTREACH_ELIGIBLE_COLUMNS: ColumnsType<Record<string, unknown>> = [
    {
        title: 'Type',
        dataIndex: 'Type',
        key: 'Type',
        width: 100,
        sorter: lrSortTextField('Type'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Description',
        dataIndex: 'Description',
        key: 'Description',
        ellipsis: true,
        sorter: lrSortTextField('Description'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'KWH Rate',
        dataIndex: 'KwhRate',
        key: 'KwhRate',
        width: 110,
        render: (v) => fmtUsdCellPreservePrecision(v),
        sorter: lrSortNumericField('KwhRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'PPW Rate',
        dataIndex: 'PpwRate',
        key: 'PpwRate',
        width: 110,
        render: (v) => fmtUsdCell(v),
        sorter: lrSortNumericField('PpwRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Escalation Rate',
        dataIndex: 'EscalationRate',
        key: 'EscalationRate',
        width: 120,
        render: (v) => fmtEscalationCell(v),
        sorter: lrSortNumericField('EscalationRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Program Type',
        dataIndex: 'ProgramType',
        key: 'ProgramType',
        width: 120,
        sorter: lrSortTextField('ProgramType'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Total EPC Payment',
        dataIndex: 'TotalEpcPayment',
        key: 'TotalEpcPayment',
        width: 140,
        render: (v) => fmtUsdCell(v),
        sorter: lrSortNumericField('TotalEpcPayment'),
        sortDirections: ['ascend', 'descend'],
    },
];

const QUOTE_TOOLS: { id: string; label: string; icon: React.ReactNode; partnerFilter?: string }[] = [
    { id: 'lightreach-products', label: 'LightReach Eligible Products', icon: <Sun size={16} weight="duotone" />, partnerFilter: 'LightReach' },
    { id: 'lightreach-battery', label: 'LightReach Battery Cap', icon: <BatteryCharging size={16} weight="duotone" />, partnerFilter: 'LightReach' },
    { id: 'roof-solar-calc', label: 'Roof / Battery Calculator', icon: <Calculator size={16} weight="duotone" /> },
    { id: 'adders', label: 'Adders', icon: <Calculator size={16} weight="duotone" /> },
    { id: 'igs-epc-calc', label: 'IGS EPC Calc', icon: <Calculator size={16} weight="duotone" />, partnerFilter: 'IGS' },
    { id: 'igs-caps', label: 'IGS Caps', icon: <Cloud size={16} weight="duotone" />, partnerFilter: 'IGS' },
    { id: 'load-calc', label: 'Load Calculator', icon: <Calculator size={16} weight="duotone" /> },
    { id: 'quote-forms', label: 'Quote Forms', icon: <FileText size={16} weight="duotone" /> },
    { id: 'popup-forms', label: 'Pop-up Forms', icon: <FolderOpen size={16} weight="duotone" /> },
];

export interface QuotesContractsCardProps {
    /** `inline` = collapsible dashboard card; `modal` = body only (parent wraps in Modal). */
    priceQuote?: any;
    layout?: 'inline' | 'modal';
    /**
     * When `layout="modal"` and this is true, quotes UI is rendered in the main dashboard Quotes section
     * (not inside Commerce Hub). Applies a panel shell + grid rhythm to match the “Quotes & contracts” modal.
     */
    embeddedInDashboardSection?: boolean;
    /** True while the quotes surface is visible (commerce hub modal open, or dashboard Quotes section). Drives RetrievePriceQuote prefetch. */
    quotesModalVisible?: boolean;
    /** After successful SubmitVersion: open agreements (e.g. commerce hub) or switch dashboard section. Overrides default store-only behavior when set. */
    onRequestOpenAgreements?: () => void;
    /** Commerce Hub modal: receive segmented quote-tools footer for `DashboardModal`. */
    onQuotesModalFooter?: (footer: React.ReactNode | null) => void;
}

export const QuotesContractsCard: React.FC<QuotesContractsCardProps> = ({
    priceQuote,
    layout = 'inline',
    embeddedInDashboardSection = false,
    quotesModalVisible = false,
    onRequestOpenAgreements,
    onQuotesModalFooter,
}) => {
    const {
        financingGrid,
        selectedFinanceCells,
        toggleFinanceCell,
        addFinanceCell,
        financePartnerSyncStatus,
        financePartnerRawSyncStatuses,
        setDashboardQuotesSummaryLines,
        theme,
        quotesRefreshTrigger,
        setQuotesRefreshTrigger,
        opportunityDetails,
        productType,
        setOpportunityDetails,
        setAgreementsRefreshTrigger,
        setAgreementsPendingExpandQuoteId,
        setCommerceHubAgreementsOpen,
        setCommerceHubQuotesOpen,
        setCommerceHubProductsOpen,
        setPriceQuoteDetails,
        setProductTypeAdders,
        onebuttonCapabilities,
    } = useOneButtonStore();

    const utilityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Utility_Info_Access');
    const isViewOnly = utilityInfoAccess === 'View';

    const generateQuoteAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Generate_Quote_Access');
    const isGenerateQuoteDisabled = generateQuoteAccess !== 'Edit' && generateQuoteAccess !== 'View';

    const generateContractAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Generate_Contract_Access');
    const showAgreementsToolbar =
        generateContractAccess === 'Edit' || generateContractAccess === 'View';
    const showOpportunityProductAdders = oneButtonCapabilityRawAllows(
        getOneButtonCapabilityValue(onebuttonCapabilities, 'Enable_Opportunity_Product_Adder'),
    );

    const viewerState = useViewerStore.getState() as any;
    const systemSizeKw = viewerState?.systemSizeKw || parseFloat(sessionStorage.getItem('System_Size') || '0') || 10.25;
    const systemWatts = systemSizeKw * 1000;
    const annualProduction = viewerState?.annualProductionKwh || parseFloat(sessionStorage.getItem('Annual_Production') || '0') || 0;

    const [savedQuotes, setSavedQuotes] = useState<QuoteRow[]>([]);
    const [addQuoteForm, setAddQuoteForm] = useState<OBDAddQuoteFormState | null>(null);
    const [quoteForm, setQuoteForm] = useState<QuoteFormState | null>(null);
    const [initialContracts, setInitialContracts] = useState<Record<string, InitialContractState>>({});
    const [quoteViewMode, setQuoteViewMode] = useState<'card' | 'table'>('card');
    const [selectedPartnerTab, setSelectedPartnerTab] = useState<string | null>(null);
    const [selectedProductTab, setSelectedProductTab] = useState<string | null>(null);
    const [quoteCurrentPage, setQuoteCurrentPage] = useState(1);
    const [detailsQuote, setDetailsQuote] = useState<{ quoteId?: string; partner: string; product: string; monthly: number; apr: number } | null>(null);
    const [sectionOpen, setSectionOpen] = useSunnyFocusedSectionOpen('quotes', {
        enabled: layout !== 'modal',
        fallbackOpen: true,
    });
    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 3 });
    const [contractActionKey, setContractActionKey] = useState<string | null>(null);
    const [contractConfirm, setContractConfirm] = useState<{ quote: QuoteRow; item: FinanceItem } | null>(null);
    const [lightreachEligibleOpen, setLightreachEligibleOpen] = useState(false);
    const [lightreachEligibleLoading, setLightreachEligibleLoading] = useState(false);
    const [lightreachEligibleRows, setLightreachEligibleRows] = useState<Record<string, unknown>[]>([]);
    const [lightreachBatteryCapOpen, setLightreachBatteryCapOpen] = useState(false);
    const [roofingCalcOpen, setRoofingCalcOpen] = useState(false);
    const [addersOpen, setAddersOpen] = useState(false);
    const [addersLoading, setAddersLoading] = useState(false);
    const [addersSaving, setAddersSaving] = useState(false);
    const [opportunityProductAdders, setOpportunityProductAdders] = useState<DealOpportunityProductAdder[]>([]);
    const addersModalRef = useRef<AddersModalHandle | null>(null);
    const [productTypeAddersReady, setProductTypeAddersReady] = useState(false);
    const [igsCapsOpen, setIgsCapsOpen] = useState(false);
    const [igsEpcCalcOpen, setIgsEpcCalcOpen] = useState(false);
    /** Sunnova system error modal — Angular `app-sunnova-system-error` parity (title `Error`). */
    const [sunnovaErrorOpen, setSunnovaErrorOpen] = useState(false);
    const [sunnovaErrorData, setSunnovaErrorData] = useState<SunnovaSystemErrorPayload | string | null>(null);
    /** Covers GetQuotes and/or RetrievePriceQuote while quotes data is loading. */
    const [quotesDataLoading, setQuotesDataLoading] = useState(false);
    const [proposalModalOpen, setProposalModalOpen] = useState(false);
    const [proposalRow, setProposalRow] = useState<ViewTrinityContractRow | null>(null);

    /** Toolbar “Add quote”: when multiple opportunity products exist, user picks which to include before Generate Quote opens. */
    const [addQuoteProductPickerOpen, setAddQuoteProductPickerOpen] = useState(false);
    const [pickSolarForAddQuote, setPickSolarForAddQuote] = useState(true);
    const [pickBatteryForAddQuote, setPickBatteryForAddQuote] = useState(true);
    const [pickRoofForAddQuote, setPickRoofForAddQuote] = useState(true);

    const setSyncStatus = useOneButtonStore((s) => s.setFinancePartnerSyncStatus);
    /**
     * Ref-counting failed here: `fetchGetQuotesAndMerge` calls `addFinanceCell`, which updates `selectedFinanceCellsKey`,
     * retriggered the modal effect, and overlapped begin/end. Only the latest load generation may clear the spinner.
     */
    const quotesLoadGenRef = useRef(0);
    const startQuotesLoad = useCallback(() => {
        const g = ++quotesLoadGenRef.current;
        setQuotesDataLoading(true);
        return g;
    }, []);
    const finishQuotesLoad = useCallback((generation: number) => {
        if (generation === quotesLoadGenRef.current) setQuotesDataLoading(false);
    }, []);

    const isModalLayout = layout === 'modal';
    const showQuotesBody = isModalLayout || sectionOpen;
    const notifClass = `obd-notification obd-theme-${theme}`;

    const opportunityIdForQuoteModal = useMemo(() => {
        const od = opportunityDetails as { Opportunity_Id?: string | number; OpportunityId?: string | number } | null | undefined;
        return od?.Opportunity_Id ?? od?.OpportunityId ?? null;
    }, [opportunityDetails]);

    const salesforceIdForQuoteModal = useMemo(() => {
        const od = opportunityDetails as { SalesForce_Id?: string; Salesforce_Id?: string } | null | undefined;
        return (
            (od?.SalesForce_Id != null && String(od.SalesForce_Id).trim()) ||
            (od?.Salesforce_Id != null && String(od.Salesforce_Id).trim()) ||
            null
        );
    }, [opportunityDetails]);

    const activeSfid = new URLSearchParams(window.location.search).get('sfid') || '';
    const hasFetchedQuotes = useRef(false);
    /** Latest RetrievePriceQuote when quotes modal/section opens — reused on SubmitVersion to avoid duplicate calls when still fresh. */
    const retrievePriceQuoteCacheRef = useRef<{ data: RetrievePriceQuoteResponse; opportunityId: string } | null>(null);
    /** Quote ids moved in this session — survives GetQuotes replace when API lags `isMovedToContract`. */
    const locallyMovedQuoteIdsRef = useRef<Set<string>>(new Set());
    const getQuotesFnRef = useRef<() => void>(() => {});

    const selectedFinanceCellsKey = useMemo(
        () => Array.from(selectedFinanceCells).sort().join('|'),
        [selectedFinanceCells],
    );

    useEffect(() => {
        locallyMovedQuoteIdsRef.current.clear();
    }, [activeSfid]);

    const selectedPartners = useMemo(() => {
        const set = new Set<string>();
        selectedFinanceCells.forEach((key) => {
            const partner = key.split('::')[0];
            if (partner) set.add(partner);
        });
        return Array.from(set);
    }, [selectedFinanceCells]);

    const hasAnyPartnerSyncComplete = useMemo(() => {
        return selectedPartners.some((p) => isPartnerSyncComplete(p, financePartnerSyncStatus[p]));
    }, [selectedPartners, financePartnerSyncStatus]);

    const availableAdderProductTypes = useMemo(
        () => getAvailableAdderProductTypesFromOpportunity(opportunityDetails, productType),
        [opportunityDetails, productType],
    );

    const isQuoteToolEnabled = useCallback(
        (tool: (typeof QUOTE_TOOLS)[0]): boolean => {
            if (isViewOnly) return false;
            if (tool.id === 'roof-solar-calc') return true;
            if (tool.id === 'adders') return productTypeAddersReady;
            if (tool.id === 'igs-caps') return true;
            if (tool.id === 'igs-epc-calc') return true;
            if (tool.id === 'lightreach-products') return true;
            if (tool.id === 'lightreach-battery') return true;
            if (hasAnyPartnerSyncComplete) return true;
            if (tool.partnerFilter && !selectedPartners.includes(tool.partnerFilter)) return false;
            return false;
        },
        [isViewOnly, productTypeAddersReady, hasAnyPartnerSyncComplete, selectedPartners],
    );

    const openAddersModal = useCallback(async () => {
        const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
        if (oid == null || String(oid).trim() === '') {
            noticeApi.warning({
                message: 'Missing opportunity',
                description: 'Unable to load deal adders without an opportunity id.',
                className: notifClass,
            });
            return;
        }

        setAddersOpen(true);
        setAddersLoading(true);
        try {
            const rows = await quoteServices.DealOpportunityProductAdders(String(oid));
            setOpportunityProductAdders(Array.isArray(rows) ? rows : []);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Request failed.';
            setOpportunityProductAdders([]);
            noticeApi.error({
                message: 'Could not load deal adders',
                description: msg,
                className: notifClass,
            });
        } finally {
            setAddersLoading(false);
        }
    }, [opportunityDetails, noticeApi, notifClass]);

    const handleSaveAdders = useCallback(async () => {
        if (!addersModalRef.current) return;
        setAddersSaving(true);
        try {
            const response = await addersModalRef.current.saveAdders();
            const responseData = response as { IsSuccessful?: boolean; Message?: string; message?: string } | undefined;
            if (responseData?.IsSuccessful === false) {
                throw new Error(String(responseData.Message ?? responseData.message ?? 'Unable to save adders.'));
            }
            noticeApi.success({
                message: 'Adders saved successfully',
                className: notifClass,
            });
            setAddersOpen(false);
        } catch (e: unknown) {
            const msg =
                String((e as any)?.response?.data?.Message ?? (e as any)?.response?.data?.message ?? '').trim() ||
                (e instanceof Error ? e.message : 'Request failed.');
            noticeApi.error({
                message: 'Could not save adders',
                description: msg,
                className: notifClass,
            });
        } finally {
            setAddersSaving(false);
        }
    }, [noticeApi, notifClass]);

    const openLightreachEligibleProducts = useCallback(async () => {
        const oid =
            opportunityDetails?.Opportunity_Id ??
            (useOneButtonStore.getState().opportunityID as string | number | undefined);
        const leadId = getLightreachLeadIdFromOpportunity(opportunityDetails);
        if (oid == null || String(oid).trim() === '') {
            noticeApi.warning({
                message: 'Missing opportunity',
                description: 'Unable to load eligible products without an opportunity id.',
                className: notifClass,
            });
            return;
        }
        if (!leadId) {
            noticeApi.warning({
                message: 'LightReach lead ID required',
                description: 'Complete LightReach lead sync so a finance partner lead ID is available.',
                className: notifClass,
            });
            return;
        }
        setLightreachEligibleOpen(true);
        setLightreachEligibleLoading(true);
        setLightreachEligibleRows([]);
        try {
            const res = await quoteServices.getLightreachPricingByAccount(oid, leadId);
            const rows = sortLightreachEligibleRows(normalizeLightreachPricingRows(res));
            setLightreachEligibleRows(rows);
        } catch (e: unknown) {
            const responseData = (e as any)?.response?.data;
            const apiMessage = String(responseData?.Message ?? responseData?.message ?? '').trim();
            const isNoRecords = apiMessage.toLowerCase() === 'no records found';

            if (isNoRecords) {
                setLightreachEligibleRows([]);
            } else {
                const msg = e instanceof Error ? e.message : 'Request failed.';
                noticeApi.error({
                    message: 'Could not load eligible products',
                    description: msg,
                    className: notifClass,
                });
            }
        } finally {
            setLightreachEligibleLoading(false);
        }
    }, [opportunityDetails, noticeApi, notifClass]);

    const onQuoteToolClick = useCallback(
        (tool: (typeof QUOTE_TOOLS)[0]) => {
            if (tool.id === 'lightreach-products') {
                void openLightreachEligibleProducts();
            }
            if (tool.id === 'lightreach-battery') {
                setLightreachBatteryCapOpen(true);
            }
            if (tool.id === 'roof-solar-calc') {
                setRoofingCalcOpen(true);
            }
            if (tool.id === 'adders') {
                void openAddersModal();
            }
            if (tool.id === 'igs-caps') {
                setIgsCapsOpen(true);
            }
            if (tool.id === 'igs-epc-calc') {
                setIgsEpcCalcOpen(true);
            }
        },
        [openLightreachEligibleProducts, openAddersModal],
    );

    const isQuoteToolVisible = useCallback(
        (tool: (typeof QUOTE_TOOLS)[0]) => {
            const products = opportunityDetails?.OpportunityProduct || [];

            if (tool.id === 'lightreach-products') {
                return lightReachEligibleProductsToolbarVisible(opportunityDetails);
            }

            if (tool.id === 'lightreach-battery') {
                const battery = products.find((p: any) => p.Product_Type_Name === 'Battery' && p.ActiveFlag);
                return (
                    (opportunityDetails?.OpportunityInterest?.Battery ||
                        opportunityDetails?.OpportunityInterest?.BatteryOnly) &&
                    battery &&
                    battery.Finance_Partner_Display_Name.toLowerCase() === 'lightreach'
                );
            }

            if (tool.id === 'roof-solar-calc') {
                return shouldShowRoofSolarCalculatorMenu(priceQuote, opportunityDetails);
            }

            if (tool.id === 'adders') {
                if (!showOpportunityProductAdders) return false;
                return availableAdderProductTypes.length > 0;
            }

            if (tool.id === 'igs-caps' || tool.id === 'igs-epc-calc') {
                const interest = opportunityDetails?.OpportunityInterest;
                const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
                const isIGS =
                    solar &&
                    ((solar.Finance_Partner_Display_Name || '').toLowerCase() === 'igs' ||
                        (solar.Finance_Partner || '').toLowerCase() === 'igs');
                return !!(priceQuote && interest?.Solar && isIGS);
            }

            return true;
        },
        [opportunityDetails, priceQuote],
    );

    const visibleQuoteTools = useMemo(
        () => QUOTE_TOOLS.filter(isQuoteToolVisible),
        [isQuoteToolVisible],
    );

    const quoteToolsGroup = useMemo(() => {
        if (visibleQuoteTools.length === 0) return null;
        return (
            <div className="obd-quotes-tools obd-metrics__action-group" role="group" aria-label="Quote tools">
                {visibleQuoteTools.map((tool) => {
                    const enabled = isQuoteToolEnabled(tool);
                    return (
                        <button
                            key={tool.id}
                            type="button"
                            className={`obd-quotes-tool-btn ${enabled ? 'obd-quotes-tool-btn--enabled' : 'obd-quotes-tool-btn--disabled'}`}
                            disabled={!enabled}
                            onClick={() => enabled && onQuoteToolClick(tool)}
                        >
                            {tool.icon}
                            <span>{tool.label}</span>
                        </button>
                    );
                })}
            </div>
        );
    }, [visibleQuoteTools, isQuoteToolEnabled, onQuoteToolClick]);

    const toolsInModalFooter = isModalLayout && !embeddedInDashboardSection;
    const toolsInFrameFooter = isModalLayout && embeddedInDashboardSection;
    const toolsInBody = !isModalLayout;

    useEffect(() => {
        if (!toolsInModalFooter || !onQuotesModalFooter) return;
        onQuotesModalFooter(quoteToolsGroup);
        return () => onQuotesModalFooter(null);
    }, [toolsInModalFooter, onQuotesModalFooter, quoteToolsGroup]);

    const fetchGetQuotesAndMerge = useCallback(async (): Promise<void> => {
        if (!activeSfid || activeSfid === '—') return;
        const response = await quoteServices.getQuotes(activeSfid);
        const data = response;
        const quotesArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [data].filter(Boolean);
        const mappedQuotes: QuoteRow[] = [];
        const addedKeys = new Set<string>();

        quotesArray.forEach((q: any) => {
            if (!q || (!q.QuoteId && !q.quoteId)) return;
            const p = q.FinancePartner || q.financePartner || 'Cash';
            const pt = q.ProductType || q.productType || 'Solar';
            const cellKey = `${p}::${pt}`;

            if (!selectedFinanceCells.has(cellKey) && !addedKeys.has(cellKey)) {
                addFinanceCell(cellKey);
                addedKeys.add(cellKey);
            }

            const qr = q as Record<string, unknown>;
            const movedFromApi = angularApiMovedToContract(qr);
            const selectedForPack = quoteVersionSelectedForContractPack(qr);
            const hasContractFromApi = truthyApiFlag(q.hasContract ?? q.HasContract);
            const lightreachPipeline =
                isLightreachFinancePartner(p) && inferLightreachPartnerContractPipeline(qr);
            const isContract = movedFromApi || hasContractFromApi || lightreachPipeline || selectedForPack;
            const isMovedToContractRow = movedFromApi || hasContractFromApi || lightreachPipeline || selectedForPack;

            mappedQuotes.push({
                id: String(q.QuoteId || q.quoteId),
                sourceKey: cellKey,
                partner: p,
                product: pt,
                pricingMethod: q.ProductType || pt,
                solarRate: q.Solar_Rate ?? 0,
                solarEscalator: 0,
                grossPpw: q.Desired_PPW_Net ?? 0,
                downPayment: 0,
                term: 25,
                apr: 0,
                monthly: parseFloat(q.MonthlyPayment) || 0,
                hasContract: isContract,
                isMovedToContract: isMovedToContractRow,
                applicationId: isContract && q.Finance_Partner_Sync_LeadId ? q.Finance_Partner_Sync_LeadId: undefined,
                applicationStatus: isContract ? 'Submitted' : undefined,
                systemSizeKw: typeof q.SystemSize !== 'undefined' ? parseFloat(q.SystemSize) : 0,
                productionKwh: typeof q.AnnualProduction !== 'undefined' ? parseFloat(q.AnnualProduction) : 0,
                quoteExpiresAt: q.QuoteExpiresIn ?? q.ExpirationDate ?? null,
                quoteCreatedStr: getTimeStamp(q.QuoteCreatedIn ?? null),
                epc: (q.TrinityEPC)? Number(q.TrinityEPC): (q.DealerEPC)? Number(q.DealerEPC) : null, 
                SunnovaQuote: q.SunnovaQuote,
                ErrorMessage: (q.ErrorMessage)? JSON.parse(q.ErrorMessage): null,
                IsQuote_Change_Order: truthyApiFlag(q.IsQuote_Change_Order ?? q.isQuote_Change_Order),
                IsQuote_Used: truthyApiFlag(q.IsQuote_Used ?? q.isQuote_Used),
                isSelectedCO: truthyApiFlag(q.isSelectedCO ?? q.IsSelectedCO),
                clonedQuoteId: q.Cloned_Quote_Id,
                financeMethodName: String(
                    q.Finance_Method_Name ?? q.finance_Method_Name ?? q.FinanceMethod ?? q.financeMethod ?? '',
                ).trim(),
                Finance_Partner_Contract_Status: q.Finance_Partner_Contract_Status,
                Price_Per_Sqft: q.Price_Per_Sqft ?? 0,
                Roof_Square_Override: q.Roof_Square_Override ?? 0,
                fnanceMethod: q.Quote_FinanceMethod

            });
        });

        setSavedQuotes((prev) => {
            const merged = mergeQuotesAfterFetch(
                mappedQuotes,
                prev,
                locallyMovedQuoteIdsRef.current,
                retrievePriceQuoteCacheRef.current?.data,
            );
            const mergedIds = new Set(merged.map((x) => x.id));
            const localsOnly = prev.filter((p) => p.id.startsWith('obd-q-') && !mergedIds.has(p.id));
            return [...merged, ...localsOnly];
        });
        setQuoteCurrentPage(1);
    }, [activeSfid, selectedFinanceCells, addFinanceCell]);

    const getTimeStamp = (date:string | null) => {
        if(date){
            return new Date(date + "Z")
                .toLocaleString(undefined, {
                    timeZone: "America/New_York",
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                })
                .replace(" ", "")
        } else return null;
    }

    const fetchGetQuotesAndMergeRef = useRef(fetchGetQuotesAndMerge);
    fetchGetQuotesAndMergeRef.current = fetchGetQuotesAndMerge;

    /** When the Commerce Hub quotes modal is open, its effect runs RetrievePriceQuote + GetQuotes and owns the loader — do not double `begin` from the debounced effect or the spinner ref never returns to 0. */
    const quotesModalOwnsLoader = isModalLayout && quotesModalVisible;

    const getQuotes = useCallback(() => {
        if (quotesModalOwnsLoader) {
            void fetchGetQuotesAndMerge().catch(console.error);
            return;
        }
        const g = startQuotesLoad();
        void fetchGetQuotesAndMerge()
            .catch(console.error)
            .finally(() => finishQuotesLoad(g));
    }, [fetchGetQuotesAndMerge, finishQuotesLoad, quotesModalOwnsLoader, startQuotesLoad]);

    getQuotesFnRef.current = getQuotes;

    useEffect(() => {
        if (!activeSfid || activeSfid === '—') return;
        if (isModalLayout && quotesModalVisible) return;
        const t = window.setTimeout(() => {
            getQuotesFnRef.current();
        }, 320);
        return () => window.clearTimeout(t);
    }, [activeSfid, selectedFinanceCellsKey, quotesRefreshTrigger, isModalLayout, quotesModalVisible]);

    /**
     * Modal: RetrievePriceQuote first (fills merge cache), then GetQuotes so quote cards match Trinity + merge rules.
     * Single loading cycle for both calls.
     */
    useEffect(() => {
        if (!isModalLayout || !quotesModalVisible) return;
        if (!activeSfid || activeSfid === '—') return;
        const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
        if (oid == null || String(oid).trim() === '') return;
        let cancelled = false;
        const t = window.setTimeout(() => {
            const loadGen = startQuotesLoad();
            void (async () => {
                try {
                    setProductTypeAddersReady(false);
                    const data = await quoteServices.getRetrievePriceQuote(String(oid));
                    setPriceQuoteDetails(data as any);
                    if (cancelled) return;
                    retrievePriceQuoteCacheRef.current = { data, opportunityId: String(oid) };
                    try {
                        const adders = await quoteServices.getProductTypesAdders();
                        if (!cancelled) {
                            setProductTypeAdders(Array.isArray(adders) ? adders : []);
                            setProductTypeAddersReady(true);
                        }
                    } catch {
                        if (!cancelled) {
                            setProductTypeAdders([]);
                            setProductTypeAddersReady(false);
                        }
                    }
                    await fetchGetQuotesAndMergeRef.current();
                } catch {
                    if (!cancelled) {
                        retrievePriceQuoteCacheRef.current = null;
                        setProductTypeAddersReady(false);
                    }
                } finally {
                    finishQuotesLoad(loadGen);
                }
            })();
        }, 320);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [
        // Do not list `selectedFinanceCellsKey`: GetQuotes merge calls `addFinanceCell`, which would retrigger this
        // effect mid-load and stack overlapping requests / spinners.
        isModalLayout,
        quotesModalVisible,
        activeSfid,
        opportunityDetails?.Opportunity_Id,
        opportunityDetails?.OpportunityId,
        quotesRefreshTrigger,
        finishQuotesLoad,
        setProductTypeAdders,
        startQuotesLoad,
    ]);

    const isActiveContract = (status?: ContractStatus) => status === 'Submitted';

    const initialPricingMethodForItem = (item: FinanceItem) => (item.isCash ? 'Cash' : 'PPW - Solar Rate');

    const hasActiveContractForMethod = (item: FinanceItem, pricingMethod: string) => {
        const init = initialContracts[item.key];
        const initMethodMatches = pricingMethod === initialPricingMethodForItem(item);
        const initActive = !!init && initMethodMatches && isActiveContract(init.status);
        const anySavedActive = savedQuotes.some(
            (q) =>
                q.sourceKey === item.key &&
                isQuoteMovedToContract(q) &&
                isActiveContract(q.applicationStatus) &&
                q.pricingMethod === pricingMethod,
        );
        return initActive || anySavedActive;
    };

    const setQuoteContract = (
        quoteId: string,
        hasContract: boolean,
        opts?: { status?: ContractStatus; applicationId?: string; isMovedToContract?: boolean },
    ) => {
        setSavedQuotes((prev) =>
            prev.map((q) => {
                if (q.id !== quoteId) return q;
                if (!hasContract) {
                    return { ...q, hasContract: false, applicationId: undefined, applicationStatus: undefined, isMovedToContract: false };
                }
                return {
                    ...q,
                    hasContract: true,
                    applicationId: opts?.applicationId ?? nextApplicationId(),
                    applicationStatus: opts?.status ?? 'Submitted',
                    isMovedToContract: opts?.isMovedToContract ?? true,
                };
            }),
        );
    };

    const removeQuote = (id: string) => { setSavedQuotes((prev) => prev.filter((q) => q.id !== id)); };

    const handleGenerateProposal = useCallback(async (q: QuoteRow) => {
        if (!opportunityIdForQuoteModal) {
            noticeApi.warning({
                message: 'Opportunity ID is missing',
                placement: 'topRight',
                className: notifClass,
            });
            return;
        }

        const gen = startQuotesLoad();
        try {
            const roofData = await designServices.RetrieveRoofProposalDetail(String(opportunityIdForQuoteModal));
            finishQuotesLoad(gen);

            const hasDetailsOverride =
                Array.isArray(roofData) &&
                roofData.length > 0 &&
                (roofData[0].Total_OneYear_Price > 0 ||
                 roofData[0].Referral_Rate > 0 ||
                 (roofData[0].Referral_Term && String(roofData[0].Referral_Term).trim() !== '') ||
                 roofData[0].Referral_APR > 0 ||
                 roofData[0].Referral_Monthly_Payment > 0);

            const openProposalModal = (manualOverride: boolean) => {
                const row: ViewTrinityContractRow = {
                    key: `proposal-${q.id}`,
                    quoteIdRaw: q.id,
                    productType: q.product,
                    productTypeCode: q.product === 'Roof Only' ? 'RO' : q.product === 'Roof' ? 'R' : undefined,
                    financePartner: q.partner,
                    financePartnerRaw: q.partner,
                    financeMethod: q.financeMethodName || q.pricingMethod || '',
                    manualOverride,
                };
                setProposalRow(row);
                setProposalModalOpen(true);
            };

            if (hasDetailsOverride) {
                Modal.confirm({
                    title: '',
                    content: "Do you want to manually override the One Year Price value with what you entered in the 'Roof Proposal Details'?",
                    okText: 'Yes',
                    cancelText: 'No',
                    closable: false,
                    maskClosable: false,
                    onOk: () => {
                        openProposalModal(true);
                    },
                    onCancel: () => {
                        openProposalModal(false);
                    },
                    className: `obd-confirm-modal obd-theme-${theme}`,
                });
            } else {
                openProposalModal(false);
            }
        } catch (err) {
            finishQuotesLoad(gen);
            // Fallback in case of error, just generate without manual override
            const row: ViewTrinityContractRow = {
                key: `proposal-${q.id}`,
                quoteIdRaw: q.id,
                productType: q.product,
                productTypeCode: q.product === 'Roof Only' ? 'RO' : q.product === 'Roof' ? 'R' : undefined,
                financePartner: q.partner,
                financePartnerRaw: q.partner,
                financeMethod: q.financeMethodName || q.pricingMethod || '',
                manualOverride: false,
            };
            setProposalRow(row);
            setProposalModalOpen(true);
        }
    }, [opportunityIdForQuoteModal, noticeApi, notifClass, theme, startQuotesLoad, finishQuotesLoad]);

    const isPersistedQuoteId = (id: string) => !!id && !id.startsWith('obd-q-');

    const refreshOpportunityAndCommerce = useCallback(async () => {
        const sfId =
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
            (activeSfid && activeSfid !== '—' ? activeSfid.trim() : '');
        setQuotesRefreshTrigger(Date.now());
        setAgreementsRefreshTrigger(Date.now());
        if (sfId) {
            try {
                const detail = await opportunityInfoServices.getOpportunityDetail(String(sfId));
                if (detail) setOpportunityDetails(detail);
            } catch {
                /* ignore */
            }
        }
    }, [
        activeSfid,
        opportunityDetails?.SalesForce_Id,
        setAgreementsRefreshTrigger,
        setOpportunityDetails,
        setQuotesRefreshTrigger,
    ]);

    const submitGoodLeapUpdateChangeOrderFromQuotes = useCallback(
        async (q: QuoteRow) => {
            const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
            if (oid == null || String(oid).trim() === '') {
                noticeApi.error({
                    message: 'Missing opportunity',
                    description: 'Cannot submit without a Trinity opportunity id.',
                    className: notifClass,
                });
                return;
            }
            if (!isPersistedQuoteId(q.id)) {
                noticeApi.warning({
                    message: 'Save this quote first',
                    description:
                        'Use “Add quote”, complete the quote form, and submit so a system quote id exists before continuing.',
                    className: notifClass,
                });
                return;
            }
            if (!goodLeapQuoteChangeOrderUiEligible(q)) {
                noticeApi.info({
                    message: 'Change order not available',
                    description: 'This quote is not marked for a GoodLeap change order yet.',
                    className: notifClass,
                });
                return;
            }

            const financeMethod = String(q.financeMethodName ?? '');
            if (!financeMethod.includes('Loan')) {
                try {
                    const r = (await quoteServices.GetOneDrawDesignReadiness(oid)) as { hasSystemLosses?: boolean };
                    if (r && !r.hasSystemLosses) {
                        Modal.warning({
                            title: '',
                            content:
                                'Design losses are missing in oneBUTTON. Please re-finalize the design in oneDRAW, then refresh and save the Design page. Once done, you can complete and submit the Change Order.',
                            okText: 'OK',
                        });
                        return;
                    }
                } catch {
                    /* non-blocking if readiness endpoint fails */
                }
            }

            const actionKey = `gl-co-${q.id}`;
            setContractActionKey(actionKey);
            try {
                setQuotesDataLoading(true);
                const res = (await quoteServices.goodLeapUpdateChangeOrder(oid, q.id));
                if(!q.isSelectedCO){
                    noticeApi.success({
                        message: 'Change order updated',
                        description: String(res?.Message ?? res?.message ?? '').trim() || 'Change order submitted.',
                        className: notifClass,
                    });
                }
                try {
                    await quoteServices.contractGetLoanStatusByOpportunity(oid);
                } catch {
                    /* optional global loan status cache */
                }
                try {
                    const freshRpq = await quoteServices.getRetrievePriceQuote(String(oid));
                    setPriceQuoteDetails(freshRpq as any);
                    retrievePriceQuoteCacheRef.current = { data: freshRpq, opportunityId: String(oid) };
                } catch {
                    retrievePriceQuoteCacheRef.current = null;
                }

                if (onRequestOpenAgreements) {
                    setAgreementsPendingExpandQuoteId(String(q.id));
                    onRequestOpenAgreements();
                } else {
                    setAgreementsPendingExpandQuoteId(String(q.id));
                    setCommerceHubAgreementsOpen(true);
                    setCommerceHubQuotesOpen(false);
                }
                await refreshOpportunityAndCommerce();
            } catch (e: unknown) {
                const ax = e as { response?: { data?: { Message?: string } }; message?: string };
                let errMsg =
                    String(ax?.response?.data?.Message ?? ax?.message ?? (e instanceof Error ? e.message : '')).trim() ||
                    'Submit change order failed.';
                try {
                    const parsed = JSON.parse(errMsg);
                    if (parsed?.message) errMsg = String(parsed.message);
                } catch {
                    /* keep errMsg */
                }
                noticeApi.error({
                    message: '',
                    description: errMsg,
                    className: notifClass,
                });
            } finally {
                setContractActionKey(null);
                setQuotesDataLoading(false);
            }
        },
        [
            noticeApi,
            notifClass,
            onRequestOpenAgreements,
            opportunityDetails?.Opportunity_Id,
            opportunityDetails?.OpportunityId,
            refreshOpportunityAndCommerce,
            setAgreementsPendingExpandQuoteId,
            setCommerceHubAgreementsOpen,
            setCommerceHubQuotesOpen,
            setPriceQuoteDetails,
        ],
    );

    const attachContractToQuote = async (q: QuoteRow, item: FinanceItem) => {
            const oid = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId;
            if (oid == null || String(oid).trim() === '') {
                noticeApi.error({
                    message: 'Missing opportunity',
                    description: 'Cannot submit without a Trinity opportunity id.',
                    className: notifClass,
                });
                return;
            }
            if (!isPersistedQuoteId(q.id)) {
                noticeApi.warning({
                    message: 'Save this quote first',
                    description:
                        'Use “Add quote”, complete the quote form, and submit so a system quote id exists before continuing.',
                    className: notifClass,
                });
                return;
            }
            if (isQuoteMovedToContract(q)) {
                noticeApi.info({
                    message: 'Already moved to contract',
                    description: `Quote #${q.id} was already moved to contract.`,
                    className: notifClass,
                });
                return;
            }

            const actionKey = `contract-${q.id}`;
            setContractActionKey(actionKey);
            try {
                const oppRecord = opportunityDetails as Record<string, unknown> | null | undefined;
                const glFpId = findGoodLeapSolarFinancePartnerId(oppRecord ?? null);
                if (glFpId != null) {
                    const tpoRes = await opportunityInfoServices.tpoRuleValidation(oid, 'Quote', glFpId);
                    const validationData = firstTpoValidationRow(tpoRes);
                    if (validationData?.Validation_Status && validationData?.Action_Type === 'Block') {
                        noticeApi.error({
                            message: 'Finance partner not available (multiplier)',
                            description:
                                validationData.Popup_Message ||
                                'TPO rule validation blocked proceeding with this quote.',
                            className: notifClass,
                        });
                        return;
                    }
                }

                const cached = retrievePriceQuoteCacheRef.current;
                const useCache =
                    cached?.opportunityId === String(oid) &&
                    Array.isArray(cached.data?.QuoteVersion) &&
                    (cached.data.QuoteVersion as unknown[]).length > 0;
                const priceQuote: RetrievePriceQuoteResponse = useCache
                    ? cached!.data
                    : await quoteServices.getRetrievePriceQuote(String(oid));
                setPriceQuoteDetails(priceQuote as any);
                if (!useCache) {
                    retrievePriceQuoteCacheRef.current = { data: priceQuote, opportunityId: String(oid) };
                }
                const rawVersions = priceQuote?.QuoteVersion;
                const versions: Record<string, unknown>[] = Array.isArray(rawVersions)
                    ? rawVersions.map((row) => ({ ...(row as Record<string, unknown>) }))
                    : [];
                normalizeQuoteVersionErrorMessages(versions);
                if (versions.length === 0) {
                    noticeApi.error({
                        message: 'No quote versions',
                        description:
                            'Could not load QuoteVersion from RetrievePriceQuote. Save or refresh quotes in Trinity and try again.',
                        className: notifClass,
                    });
                    return;
                }

                const qidNum = Number(q.id);
                versions.forEach((row: Record<string, unknown>) => {
                    const rid = Number(row.QuoteId ?? row.quoteId);
                    if (Number.isFinite(qidNum) && rid === qidNum) row.Selected = true;
                });

                const sfRaw =
                    priceQuote?.SalesForce_Id ??
                    (opportunityDetails as { SalesForce_Id?: string })?.SalesForce_Id ??
                    activeSfid;
                const salesForceId = sfRaw != null && String(sfRaw).trim() !== '' && String(sfRaw) !== '—' ? String(sfRaw).trim() : '';
                if (!salesForceId) {
                    noticeApi.error({
                        message: 'Missing Salesforce id',
                        description: 'SubmitVersion requires SalesForce_Id on the opportunity.',
                        className: notifClass,
                    });
                    return;
                }

                const opportunityPayloadId =
                    priceQuote?.Opportunity_Id ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? oid;

                await quoteServices.submitVersion({
                    Opportunity_Id: opportunityPayloadId as string | number,
                    QuoteVersion: versions,
                    SalesForce_Id: salesForceId,
                });

                try {
                    const freshRpq = await quoteServices.getRetrievePriceQuote(String(oid));
                    setPriceQuoteDetails(freshRpq as any);
                    retrievePriceQuoteCacheRef.current = { data: freshRpq, opportunityId: String(oid) };
                } catch {
                    retrievePriceQuoteCacheRef.current = null;
                }

                locallyMovedQuoteIdsRef.current.add(String(q.id));
                setQuoteContract(q.id, true, {
                    status: 'Submitted',
                    applicationId: `APP-${q.id}`,
                    isMovedToContract: true,
                });

                if (onRequestOpenAgreements) {
                    onRequestOpenAgreements();
                } else {
                    setCommerceHubAgreementsOpen(true);
                    setCommerceHubQuotesOpen(false);
                }

                await refreshOpportunityAndCommerce();

                noticeApi.success({
                    message: 'Quote version submitted',
                    description: `${formatPartnerDisplayName(item.partner)} · ${item.product} · quote #${q.id}`,
                    className: notifClass,
                });
            } catch (e: unknown) {
                const msg =
                    e && typeof e === 'object' && 'response' in e
                        ? String((e as any)?.response?.data?.message ?? (e as any)?.response?.data?.Message ?? '')
                        : '';
                const fallback = e instanceof Error ? e.message : 'Submit version failed.';
                noticeApi.error({
                    message: 'Quote',
                    description: msg || fallback,
                    className: notifClass,
                });
            } finally {
                setContractActionKey(null);
            }
    };

    /**
     * Same API flow as Angular generate-quote **Next**: `TPORuleValidation` (Good Leap solar) when applicable,
     * then `SubmitVersion` with `RetrievePriceQuote` QuoteVersion — not `FinancePartner/contract`.
     */
    const confirmAttachContractToQuote = (q: QuoteRow, item: FinanceItem) => {
        if (isQuoteMovedToContract(q)) {
            noticeApi.info({
                message: 'Already moved to contract',
                description: `Quote #${q.id} was already moved to contract.`,
                className: notifClass,
            });
            return;
        }
        setContractConfirm({ quote: q, item });
    };

    const confirmAttachContract = () => {
        if (!contractConfirm) return;
        void attachContractToQuote(contractConfirm.quote, contractConfirm.item).finally(() => setContractConfirm(null));
    };

    const updateAddQuoteForm = (field: keyof OBDAddQuoteFormState, value: string) => {
        setAddQuoteForm((prev) => (prev ? { ...prev, [field]: value } : null));
    };

    const items: FinanceItem[] = useMemo(
        () =>
            Array.from(selectedFinanceCells)
                .map((key) => {
                    const [partner, product] = key.split('::');
                    const row = financingGrid.find((r) => r.partner === partner);
                    let cell = row?.prices[product as FinancingProduct];
                    if (!cell) {
                        const quote = savedQuotes.find((q) => q.sourceKey === key);
                        cell = { monthly: quote?.monthly || 0, term: quote?.term || 25, apr: quote?.apr || 0 } as PriceCell;
                    }
                    return { key, partner, product, isCash: partner === 'Cash', ...cell };
                })
                .filter(Boolean) as FinanceItem[],
        [selectedFinanceCells, financingGrid, savedQuotes],
    );

    const dashboardSummaryLines = useMemo(
        () => buildDashboardQuotesSummaryLines(savedQuotes, initialContracts, items),
        [savedQuotes, initialContracts, items],
    );

    useEffect(() => {
        setDashboardQuotesSummaryLines(dashboardSummaryLines);
    }, [dashboardSummaryLines, setDashboardQuotesSummaryLines]);

    useEffect(
        () => () => setDashboardQuotesSummaryLines({ selectedQuoteLine: null, signedContractLine: null }),
        [setDashboardQuotesSummaryLines],
    );

    const partnersWithQuotes = useMemo(() => {
            const set = new Set<string>();
            savedQuotes.forEach((q) => set.add(q.partner));
            items.forEach((i) => set.add(i.partner));
            return Array.from(set).sort((a, b) => formatPartnerDisplayName(a).localeCompare(formatPartnerDisplayName(b)));
        }, [savedQuotes, items]);

    const productsWithQuotes = useMemo(() => {
        const set = new Set<string>();
        savedQuotes.forEach((q) => set.add(q.product));
        items.forEach((i) => set.add(i.product));
        const list = Array.from(set);
        return list.sort((a, b) => {
            const ai = QUOTE_PRODUCT_TAB_ORDER.indexOf(a as FinancingProduct);
            const bi = QUOTE_PRODUCT_TAB_ORDER.indexOf(b as FinancingProduct);
            if (ai === -1 && bi === -1) return a.localeCompare(b);
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
    }, [savedQuotes, items]);

    const filteredItems = useMemo(() => {
        let out = items;
        if (selectedPartnerTab) out = out.filter((i) => i.partner === selectedPartnerTab);
        if (selectedProductTab) out = out.filter((i) => i.product === selectedProductTab);
        return out;
    }, [items, selectedPartnerTab, selectedProductTab]);

    type DisplayRecord = { type: 'initial' | 'quote'; item: FinanceItem; quote?: QuoteRow };
    const displayRecords = useMemo((): DisplayRecord[] => {
        const out: DisplayRecord[] = [];
        savedQuotes.forEach((q) => {
            filteredItems.forEach((item) => {
                if(q.sourceKey === item.key){
                    out.push({ type: 'quote', item, quote: q });
                } 
            });
        });
        filteredItems.forEach((item) => {
            const itemQuotes = savedQuotes.filter((q) => q.sourceKey === item.key);
            if (itemQuotes.length === 0) out.push({ type: 'initial', item });
        })
        return out;
    }, [filteredItems, savedQuotes]);

    const totalRecords = displayRecords.length;
    const paginatedRecords = useMemo(
        () =>
            displayRecords.slice(
                (quoteCurrentPage - 1) * QUOTES_PAGE_SIZE,
                quoteCurrentPage * QUOTES_PAGE_SIZE,
            ),
        [displayRecords, quoteCurrentPage],
    );

    const onPartnerTabSelect = (partner: string | null) => {
        setSelectedPartnerTab(partner);
        setQuoteCurrentPage(1);
    };

    const onProductTabSelect = (product: string | null) => {
        setSelectedProductTab(product);
        setQuoteCurrentPage(1);
    };

    const runPartnerSyncPreflightForAddQuote = useCallback(async (): Promise<boolean> => {
        if (!opportunityDetails?.OpportunityProduct) return false;

        let haveAllProductIsGoodLeap = true;
        opportunityDetails.OpportunityProduct.forEach((opportunityProduct: any) => {
            if (opportunityProduct.Finance_Partner_Display_Name != 'Good Leap' && opportunityProduct.ActiveFlag) {
                haveAllProductIsGoodLeap = false;
            }
        });

        let haveAllProductIsCash = true;
        opportunityDetails.OpportunityProduct.forEach((opportunityProduct: any) => {
            if (opportunityProduct.Finance_Partner_Display_Name != 'Cash' && opportunityProduct.ActiveFlag) {
                haveAllProductIsCash = false;
            }
        });
        let haveAnyIGSLightreach = false;
        opportunityDetails.OpportunityProduct.forEach((opportunityProduct: any) => {
            if (
                (opportunityProduct.Finance_Partner_Display_Name == 'IGS' ||
                    opportunityProduct.Finance_Partner_Display_Name.toLowerCase() == 'lightreach') &&
                opportunityProduct.ActiveFlag
            ) {
                haveAnyIGSLightreach = true;
            }
        });

        if (!hasAnyPartnerSyncComplete && !haveAllProductIsGoodLeap && !haveAllProductIsCash) {
            if (JSON.stringify(financePartnerSyncStatus) == '{}' || financePartnerSyncStatus == null) {
                const currentOppId = useOneButtonStore.getState().opportunityID;
                const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
                const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);
                if (Array.isArray(syncList)) {
                    if (haveAnyIGSLightreach && syncList.length > 0) {
                        const statusObj = syncList[0];
                        const solarPartnerName =
                            opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag)
                                ?.Finance_Partner_Display_Name ?? '';
                        const newStatus: Partial<PartnerSyncChecklist> = {
                            lead: statusObj.IsLeadCreate === true,
                            contact: statusObj.IsContactSync === true,
                            titleCheck: statusObj.IsTilte === true || statusObj.IsTitle === true,
                            utility: statusObj.IsUtility === true,
                            leadSystem: statusObj.IsLeadSystem === true,
                            creditCheck: statusObj.IsCreditPass === true,
                        };

                        setSyncStatus(solarPartnerName, newStatus);
                        if (
                            newStatus.lead === false ||
                            newStatus.contact === false ||
                            newStatus.utility === false ||
                            newStatus.leadSystem === false
                        ) {
                            noticeApi.warning({
                                message: 'Finance Partner Sync required',
                                description:
                                    'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                                className: notifClass,
                            });
                            return false;
                        }
                    }
                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Sync required',
                        description:
                            'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                        className: notifClass,
                    });
                    return false;
                }
            } else {
                const syncList = Object.values(financePartnerRawSyncStatuses) || [];

                if (Array.isArray(syncList)) {
                    if (haveAnyIGSLightreach && syncList.length > 0) {
                        const statusObj: any = syncList[0];
                        const solarPartnerName =
                            opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag)
                                ?.Finance_Partner_Display_Name ?? '';
                        const newStatus: Partial<PartnerSyncChecklist> = {
                            lead: statusObj.IsLeadCreate === true,
                            contact: statusObj.IsContactSync === true,
                            titleCheck: statusObj.IsTilte === true || statusObj.IsTitle === true,
                            utility: statusObj.IsUtility === true,
                            leadSystem: statusObj.IsLeadSystem === true,
                            creditCheck: statusObj.IsCreditPass === true,
                        };
                        setSyncStatus(solarPartnerName, newStatus);
                        if (
                            newStatus.lead === false ||
                            newStatus.contact === false ||
                            newStatus.utility === false ||
                            newStatus.leadSystem === false
                        ) {
                            noticeApi.warning({
                                message: 'Finance Partner Sync required',
                                description:
                                    'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                                className: notifClass,
                            });
                            return false;
                        }
                    }
                } else {
                    noticeApi.warning({
                        message: 'Finance Partner Sync required',
                        description:
                            'Complete the partner checklist for your selected finance partner(s) before adding quotes.',
                        className: notifClass,
                    });
                    return false;
                }
            }
        }
        return true;
    }, [
        opportunityDetails,
        hasAnyPartnerSyncComplete,
        financePartnerSyncStatus,
        financePartnerRawSyncStatuses,
        noticeApi,
        notifClass,
        setSyncStatus,
    ]);

    const buildToolbarGenerateQuoteData = useCallback(
        (includeSolar: boolean, includeBattery: boolean, includeRoof: boolean): selectedQuote | null => {
            if (!opportunityDetails?.OpportunityProduct) return null;
            const activeProducts = opportunityDetails.OpportunityProduct.filter((p: any) => p.ActiveFlag);
            const solarProduct = includeSolar ? activeProducts.find((p: any) => p.Product_Type_Name === 'Solar') : undefined;
            const batteryProduct = includeBattery ? activeProducts.find((p: any) => p.Product_Type_Name === 'Battery') : undefined;
            const roofProduct = includeRoof ? activeProducts.find((p: any) => p.Product_Type_Name === 'Roof') : undefined;

            const productParts: string[] = [];
            if (solarProduct) productParts.push('Solar');
            if (batteryProduct) productParts.push('Battery');
            if (roofProduct) productParts.push('Roof');
            if (productParts.length === 0) return null;

            const combinedProduct = productParts.join(' + ');
            const primaryProduct = solarProduct || batteryProduct || roofProduct;
            if (!primaryProduct) return null;

            const data: selectedQuote = {
                product: combinedProduct,
                products: [],
                partner: primaryProduct.Finance_Partner_Display_Name,
                methord: primaryProduct.Finance_Method_Name,
                price: null,
                ExcelOffer: null,
                payload: null,
            };
            if (solarProduct) {
                data.products!.push({
                    product: 'Solar',
                    partner: solarProduct.Finance_Partner_Display_Name,
                    methord: solarProduct.Finance_Method_Name,
                    price: null,
                    ExcelOffer: null,
                    payload: null,
                });
            }
            if (batteryProduct) {
                data.products!.push({
                    product: 'Battery',
                    partner: batteryProduct.Finance_Partner_Display_Name,
                    methord: batteryProduct.Finance_Partner_Display_Name == 'Cash' ? null : batteryProduct.Finance_Method_Name,
                    price: null,
                    ExcelOffer: null,
                    payload: null,
                });
            }
            if (roofProduct) {
                data.products!.push({
                    product: 'Roof',
                    partner: roofProduct.Finance_Partner_Display_Name,
                    methord: roofProduct.Finance_Method_Name,
                    price: null,
                    ExcelOffer: null,
                    payload: null,
                });
            }
            return data;
        },
        [opportunityDetails, showOpportunityProductAdders, availableAdderProductTypes],
    );

    const confirmToolbarAddQuoteProductSelection = useCallback(() => {
        if (!pickSolarForAddQuote && !pickBatteryForAddQuote && !pickRoofForAddQuote) {
            noticeApi.warning({
                message: 'Select at least one product',
                description: 'Choose which products to include in this quote.',
                className: notifClass,
            });
            return;
        }
        const data = buildToolbarGenerateQuoteData(pickSolarForAddQuote, pickBatteryForAddQuote, pickRoofForAddQuote);
        setAddQuoteProductPickerOpen(false);
        if (data) {
            setConfirmQuoteCellKey(data);
        } else {
            noticeApi.info({
                message: 'No active products',
                description: 'Ensure a product is active in Opportunity Details to add quotes.',
                className: notifClass,
            });
        }
    }, [
        pickSolarForAddQuote,
        pickBatteryForAddQuote,
        pickRoofForAddQuote,
        buildToolbarGenerateQuoteData,
        noticeApi,
        notifClass,
    ]);

    const openAddQuoteModal = useCallback(
        async (item: FinanceItem, quote:any) => {
            if (!opportunityDetails?.OpportunityProduct) return;
            try {
                const syncOk = await runPartnerSyncPreflightForAddQuote();
                if (!syncOk) return;
                if(item.product == 'Solar' || item.product == 'Battery' || item.product == 'Roof' || item.product == "Roof Only" || item.product == "Battery Only"){
                    if(item.product == "Roof Only") item.product = "Roof";
                    if(item.product == "Battery Only") item.product = "Battery";

                    const quoteDetails  = opportunityDetails.OpportunityProduct.find((ele:any) =>  ele.Product_Type_Name == item.product 
                    && (ele.Finance_Partner_Display_Name == item.partner  || ele.Finance_Partner == item.partner )
                    && ele.ActiveFlag);

                    if(!quoteDetails){
                        noticeApi.warning({
                            message: 'Unable to open quote form',
                            description:
                                'This row’s partner and product could not be matched to Opportunity Details. Confirm the product is active and the partner matches the financing line.',
                            className: notifClass,
                        });
                        return;
                    }

                    let data:any = buildToolbarGenerateQuoteData((item.product == 'Solar'), (item.product == 'Battery'), (item.product == 'Roof'));
                    if(data){
                        const clonedQuoteDetails = await quoteServices.getQuoteDetails(quote.id)
                        data.clonedQuoteDetails = clonedQuoteDetails.data;
                        setConfirmQuoteCellKey(data);
                    }
                }
            } catch (err) {
                console.error('Add quote failed:', err);
                noticeApi.error({
                    message: 'Unable to add quote',
                    description: err instanceof Error ? err.message : 'An unexpected error occurred.',
                    className: notifClass,
                });
            }
        },
        [opportunityDetails, financingGrid, noticeApi, notifClass, runPartnerSyncPreflightForAddQuote],
    );

    const submitAddQuoteForm = () => {
        if (!addQuoteForm) return;
        const monthly = formToMonthly(addQuoteForm, systemWatts);
        const newRow: QuoteRow = {
            id: nextQuoteId(),
            sourceKey: addQuoteForm.sourceKey,
            partner: addQuoteForm.partner,
            product: addQuoteForm.product,
            pricingMethod: addQuoteForm.pricingMethod,
            solarRate: parseFloat(addQuoteForm.solarRate) || 0,
            solarEscalator: parseFloat(addQuoteForm.solarEscalator) || 0,
            grossPpw: parseFloat(addQuoteForm.grossPpw) || 0,
            downPayment: parseFloat(addQuoteForm.downPayment) || 0,
            term: parseInt(addQuoteForm.term) || 25,
            apr: parseFloat(addQuoteForm.apr) || 0,
            monthly,
            hasContract: false,
            systemSizeKw,
            productionKwh: annualProduction,
            epc: null,
            SunnovaQuote: "",
            ErrorMessage: null,
            IsQuote_Used: false,
            IsQuote_Change_Order: false,
            isSelectedCO: false,
            financeMethodName: ''
        };
        setSavedQuotes((prev) => [...prev, newRow]);
        setAddQuoteForm(null);
    };

    const totalQuotes = savedQuotes.length;
    const liveMonthly = addQuoteForm ? formToMonthly(addQuoteForm, systemWatts) : 0;

    const addQuoteProductsOnOpportunity = useMemo(() => {
        const active = opportunityDetails?.OpportunityProduct?.filter((p: any) => p.ActiveFlag) ?? [];
        return {
            solar: !!active.find((p: any) => p.Product_Type_Name === 'Solar'),
            battery: !!active.find((p: any) => p.Product_Type_Name === 'Battery'),
            roof: !!active.find((p: any) => p.Product_Type_Name === 'Roof'),
        };
    }, [opportunityDetails]);

    /** Active product rows from Opportunity Details (Products / Commerce Hub). */
    const addQuoteOpportunityProductRows = useMemo(() => {
        const active = opportunityDetails?.OpportunityProduct?.filter((p: any) => p.ActiveFlag) ?? [];
        const solar = active.find((p: any) => p.Product_Type_Name === 'Solar') as Record<string, unknown> | undefined;
        const battery = active.find((p: any) => p.Product_Type_Name === 'Battery') as Record<string, unknown> | undefined;
        const roof = active.find((p: any) => p.Product_Type_Name === 'Roof') as Record<string, unknown> | undefined;
        return { solar, battery, roof };
    }, [opportunityDetails]);

    const solarAndBatterySamePartner = useMemo(
        () =>
            solarAndBatteryShareFinancePartner(
                addQuoteOpportunityProductRows.solar,
                addQuoteOpportunityProductRows.battery,
            ),
        [addQuoteOpportunityProductRows.solar, addQuoteOpportunityProductRows.battery],
    );

    /** When Solar + Battery share a partner, Battery requires Solar in this quote; otherwise Battery-only is allowed. */
    const batteryRequiresSolarForAddQuote = useMemo(
        () =>
            addQuoteProductsOnOpportunity.solar &&
            addQuoteProductsOnOpportunity.battery &&
            solarAndBatterySamePartner &&
            normalizePartnerKeyForAddQuoteCompare(
                partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.battery),
            ) !== 'cash',
        [
            addQuoteProductsOnOpportunity.solar,
            addQuoteProductsOnOpportunity.battery,
            solarAndBatterySamePartner,
            addQuoteOpportunityProductRows.battery,
        ],
    );

    const batteryAddQuoteCheckboxEnabled = useMemo(() => {
        if (!addQuoteProductsOnOpportunity.battery) return false;
        const batteryPartner = normalizePartnerKeyForAddQuoteCompare(
            partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.battery),
        );
        if (batteryPartner === 'cash') return true;
        if (!addQuoteProductsOnOpportunity.solar) return true;
        if (!solarAndBatterySamePartner) return true;
        return pickSolarForAddQuote;
    }, [
        addQuoteProductsOnOpportunity.battery,
        addQuoteProductsOnOpportunity.solar,
        addQuoteOpportunityProductRows.battery,
        solarAndBatterySamePartner,
        pickSolarForAddQuote,
    ]);

    const SBLightReach = useMemo(() => {
        const products = opportunityDetails?.OpportunityProduct;
        if (!products) return false;
        const solarProduct = products.find(
            (el: any) => el.Product_Type_Name === 'Solar' && el.Finance_Partner === 'Lightreach' && el.ActiveFlag,
        );
        const batteryProduct = products.find(
            (el: any) => el.Product_Type_Name === 'Battery' && el.Finance_Partner === 'Lightreach' && el.ActiveFlag,
        );
        return !!(solarProduct && batteryProduct);
    }, [opportunityDetails]);

    const SBGoodleapSingleQuote = useMemo(() => {
        const products = opportunityDetails?.OpportunityProduct;
        if (!products) return false;
        const solarProduct = products.find(
            (el: any) => el.Product_Type_Name === 'Solar' && el.Finance_Partner === 'Good Leap' && el.ActiveFlag,
        );
        const batteryProduct = products.find(
            (el: any) => el.Product_Type_Name === 'Battery' && el.Finance_Partner === 'Good Leap' && el.ActiveFlag,
        );

        if (solarProduct && batteryProduct) {
            return (
                solarProduct.Finance_Method_Name === batteryProduct.Finance_Method_Name &&
                (solarProduct.Finance_Method_Name == 'Lease Storage' || solarProduct.Finance_Method_Name == 'Loan Storage')
            );
        }
        return false;
    }, [opportunityDetails]);


    useEffect(() => {
        if (!addQuoteProductPickerOpen) return;
        if (!batteryAddQuoteCheckboxEnabled) setPickBatteryForAddQuote(false);
    }, [addQuoteProductPickerOpen, batteryAddQuoteCheckboxEnabled]);

    const handlePickSolarForAddQuoteChange = useCallback(
        (e: CheckboxChangeEvent) => {
            const checked = e.target.checked;
            setPickSolarForAddQuote(checked);
            if (batteryRequiresSolarForAddQuote) {
                setPickBatteryForAddQuote(checked);
            }
        },
        [batteryRequiresSolarForAddQuote],
    );

    const handlePickBatteryForAddQuoteChange = useCallback((e: CheckboxChangeEvent) => {
        setPickBatteryForAddQuote(e.target.checked);
    }, []);

    const handlePickRoofForAddQuoteChange = useCallback((e: CheckboxChangeEvent) => {
        setPickRoofForAddQuote(e.target.checked);
    }, []);

    const toolbarAddQuote = useCallback(async () => {
        if (!opportunityDetails?.OpportunityProduct) return;
        try {
            const syncOk = await runPartnerSyncPreflightForAddQuote();
            if (!syncOk) return;

            const activeProducts = opportunityDetails.OpportunityProduct.filter((p: any) => p.ActiveFlag);
            const solarProduct = activeProducts.find((p: any) => p.Product_Type_Name === 'Solar');
            const batteryProduct = activeProducts.find((p: any) => p.Product_Type_Name === 'Battery');
            const roofProduct = activeProducts.find((p: any) => p.Product_Type_Name === 'Roof');
            const nKinds = (solarProduct ? 1 : 0) + (batteryProduct ? 1 : 0) + (roofProduct ? 1 : 0);
            if (nKinds === 0) {
                noticeApi.info({
                    message: 'No active products',
                    description: 'Ensure a product is active in Opportunity Details to add quotes.',
                    className: notifClass,
                });
                return;
            }
            if (nKinds > 1) {
                setPickSolarForAddQuote(!!solarProduct);
                const solarRow = solarProduct as Record<string, unknown> | undefined;
                const batteryRow = batteryProduct as Record<string, unknown> | undefined;
                const samePartner = solarAndBatteryShareFinancePartner(solarRow, batteryRow);
                const batteryPartnerKey = normalizePartnerKeyForAddQuoteCompare(
                    partnerDisplayNameFromOpportunityProduct(batteryRow),
                );
                let batteryOk = !!batteryProduct;
                if (batteryProduct && solarProduct && samePartner) {
                    batteryOk =
                        batteryPartnerKey === 'cash' ||
                        isGoodLeapOrLightreachSolarFinancing(partnerDisplayNameFromOpportunityProduct(solarRow));
                }
                setPickBatteryForAddQuote(batteryOk);
                setPickRoofForAddQuote(!!roofProduct);
                setAddQuoteProductPickerOpen(true);
                return;
            }
            const data = buildToolbarGenerateQuoteData(!!solarProduct, !!batteryProduct, !!roofProduct);
            if (data) setConfirmQuoteCellKey(data);
        } catch (err) {
            console.error('Add quote failed:', err);
            noticeApi.error({
                message: 'Unable to add quote',
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
                className: notifClass,
            });
        }
    }, [
        opportunityDetails,
        runPartnerSyncPreflightForAddQuote,
        buildToolbarGenerateQuoteData,
        noticeApi,
        notifClass,
    ]);

    const openAgreementsFromToolbar = useCallback(() => {
        if (onRequestOpenAgreements) {
            onRequestOpenAgreements();
            return;
        }
        setCommerceHubAgreementsOpen(true);
        setCommerceHubQuotesOpen(false);
    }, [onRequestOpenAgreements, setCommerceHubAgreementsOpen, setCommerceHubQuotesOpen]);

    const quotesToolbar = isModalLayout ? (
        <div className="obd-quotes-modal__toolbar">
            <div className="obd-quotes-modal__toolbar-actions">
                <span className="obd-financing__chip obd-quotes-modal__chip">
                    {items.length} selected
                    {totalQuotes > 0 ? ` · ${totalQuotes} quote${totalQuotes > 1 ? 's' : ''}` : ''}
                </span>
                {!isGenerateQuoteDisabled && (
                    <Button type="default" onClick={toolbarAddQuote}>
                        <Plus size={16} weight="bold" className="obd-quotes-modal__btn-icon" aria-hidden />
                        Add quote
                    </Button>
                )}
                <Button
                    type="default"
                    data-obd-guide="commerce-products"
                    onClick={() => setCommerceHubProductsOpen(true, { keepQuotesOpen: true })}
                >
                    <Cube size={16} weight="duotone" className="obd-quotes-modal__btn-icon" aria-hidden />
                    Products
                </Button>
                {showAgreementsToolbar ? (
                    <Button
                        type="default"
                        data-obd-guide="commerce-agreements"
                        onClick={openAgreementsFromToolbar}
                    >
                        <Article size={16} weight="bold" className="obd-quotes-modal__btn-icon" aria-hidden />
                        Agreements
                    </Button>
                ) : null}
            </div>
        </div>
    ) : null;

    const quotesViewToggle = (
        <div className="obd-quotes-toolbar" role="group" aria-label="Quote list view">
            <div className="obd-quotes-view-toggle">
                <button
                    type="button"
                    className={`obd-quotes-view-btn ${quoteViewMode === 'card' ? 'obd-quotes-view-btn--active' : ''}`}
                    onClick={() => setQuoteViewMode('card')}
                    title="Card view"
                >
                    <SquaresFour size={16} weight="regular" />
                    <span>Card</span>
                </button>
                <button
                    type="button"
                    className={`obd-quotes-view-btn ${quoteViewMode === 'table' ? 'obd-quotes-view-btn--active' : ''}`}
                    onClick={() => setQuoteViewMode('table')}
                    title="Table view"
                >
                    <Table size={16} weight="regular" />
                    <span>Table</span>
                </button>
            </div>
        </div>
    );

    const quotesMainBody = (
        <>
            <div className="obd-selected-layout">
                <div className="obd-selected-layout__quotes">
                    {toolsInBody ? quoteToolsGroup : null}
                    {items.length === 0 && (
                        <div className="obd-quotes__empty-hint" style={{ marginTop: 10 }}>
                            Select one or more pricing cells in <strong>Financing Options</strong> to manage quotes and contracts here.
                        </div>
                    )}
                    <div
                        className={`obd-quotes-filter-bar${
                            partnersWithQuotes.length === 0 && productsWithQuotes.length === 0
                                ? ' obd-quotes-filter-bar--view-only'
                                : ''
                        }`}
                    >
                        {(partnersWithQuotes.length > 0 || productsWithQuotes.length > 0) && (
                            <div className="obd-quotes-filter-bar__tabs">
                                {partnersWithQuotes.length > 0 ? (
                                    <div
                                        className="obd-quotes-partner-tabs obd-metrics__action-group"
                                        role="group"
                                        aria-label="Finance partner"
                                    >
                                        <button
                                            type="button"
                                            className={`obd-quotes-filter-btn ${selectedPartnerTab === null ? 'obd-quotes-filter-btn--active' : ''}`}
                                            onClick={() => onPartnerTabSelect(null)}
                                        >
                                            All
                                        </button>
                                        {partnersWithQuotes.map((partner) => (
                                            <button
                                                key={partner}
                                                type="button"
                                                className={`obd-quotes-filter-btn ${selectedPartnerTab === partner ? 'obd-quotes-filter-btn--active' : ''}`}
                                                onClick={() => onPartnerTabSelect(partner)}
                                            >
                                                {formatPartnerDisplayName(partner)}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                                {productsWithQuotes.length > 0 ? (
                                    <div
                                        className="obd-quotes-product-tabs obd-metrics__action-group"
                                        role="group"
                                        aria-label="Product"
                                    >
                                        <button
                                            type="button"
                                            className={`obd-quotes-filter-btn ${selectedProductTab === null ? 'obd-quotes-filter-btn--active' : ''}`}
                                            onClick={() => onProductTabSelect(null)}
                                        >
                                            All
                                        </button>
                                        {productsWithQuotes.map((product) => (
                                            <button
                                                key={product}
                                                type="button"
                                                className={`obd-quotes-filter-btn ${selectedProductTab === product ? 'obd-quotes-filter-btn--active' : ''}`}
                                                onClick={() => onProductTabSelect(product)}
                                            >
                                                {product}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {quotesViewToggle}
                    </div>

                    {/* Quotes list/table remains below; it will naturally render empty state when no items. */}
                                <div className={`obd-quotes-grid ${quoteViewMode === 'table' ? 'obd-quotes-grid--table' : 'obd-quotes-grid--cards'}`}>
                                    {quoteViewMode === 'table' ? (
                                        <div className="obd-quotes-table-wrap">
                                            <table className="obd-quotes-table">
                                                <thead>
                                                    <tr>
                                                        <th>Product</th>
                                                        <th>Quote #</th>
                                                        <th>Finance Method</th>
                                                        <th>Monthly / EPC</th>
                                                        <th>Solar Rate / Roof Squares</th>
                                                        <th>Gross PPW / Desired PPW / Price/Sqft</th>
                                                        <th>Total EPC(Net)</th>
                                                        <th>System Size</th>
                                                        <th>Prod</th>
                                                        <th>Status</th>
                                                        <th style={{minWidth: '150px'}}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedRecords.map((rec) => {
                                                        const item = rec.item;
                                                        const initialApp = initialContracts[item.key];
                                                        if (rec.type === 'initial') {
                                                            return (
                                                                <tr key={`initial-${item.key}`} className={initialApp ? 'obd-quotes-table__row--hl' : ''}>
                                                                    <td><span className="obd-quotes-table__product">{item.partner} · {item.product}</span></td>
                                                                    <td>—</td>
                                                                    <td></td>
                                                                    <td>
                                                                        <span className="obd-quote-card__val obd-quote-card__val--cost">
                                                                            ${item.isCash ? item.monthly.toLocaleString() : item.monthly.toFixed(2)}{!item.isCash ? '/mo' : ''}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span>

                                                                        </span>
                                                                    </td>
                                                                    <td>

                                                                    </td>
                                                                    <td>

                                                                    </td>
                                                                    <td>{systemSizeKw} kW</td>
                                                                    <td>{annualProduction.toLocaleString()} kWh</td>
                                                                    <td>
                                                                        {initialApp ? (
                                                                            <span className={`obd-app-tag ${initialApp.status === 'Cancelled' ? 'obd-app-tag--cancelled' : 'obd-app-tag--submitted'}`}>
                                                                                {initialApp.applicationId} · {initialApp.status}
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        <div className="obd-quote-row__footer-actions" style={{ display: 'flex', gap: 8, marginTop: 0, paddingTop: 0, border: 'none', justifyContent: 'center' }}>
                                                                            <button type="button" className="obd-quote-action obd-quote-action--icon" 
                                                                                onClick={() => setDetailsQuote({ partner: item.partner, product: item.product, monthly: item.monthly, apr: item.apr })} 
                                                                                title="View Quote Deails">
                                                                                <Eye size={18} weight="duotone" />
                                                                            </button>
                                                                            {!initialApp && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="obd-quote-action obd-quote-action--icon obd-quote-action--primary"
                                                                                    onClick={() =>
                                                                                        noticeApi.info({
                                                                                            message: 'Generate a quote first',
                                                                                            description:
                                                                                                'Use “Add quote” and submit a saved quote before creating a contract.',
                                                                                            className: notifClass,
                                                                                        })
                                                                                    }
                                                                                    title="New Contract"
                                                                                    aria-label="New Contract"
                                                                                >
                                                                                    <SealCheck size={18} weight="duotone" />
                                                                                </button>
                                                                            )}
                                                                            {!isGenerateQuoteDisabled && (
                                                                                <button type="button" className="obd-quote-action obd-quote-action--icon" onClick={() => openAddQuoteModal(item, q)} title="Clone quote" aria-label="Add quote">
                                                                                    <Plus size={18} weight="bold" /> 
                                                                                </button>
                                                                            )}
                                                                            <button type="button" className="obd-quote-action obd-quote-action--icon obd-quote-action--danger" onClick={() => toggleFinanceCell(item.key)} title="Remove" aria-label="Remove">
                                                                                <XIcon size={18} weight="bold" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                        const q = rec.quote!;
                                                        const contractAttached = isQuoteMovedToContract(q);
                                                        const glCoEligible = goodLeapQuoteChangeOrderUiEligible(q);
                                                        const primaryLabel = 'New Contract';
                                                        const sysKw = (q.systemSizeKw ?? systemSizeKw);
                                                        const prodKwh = (q.productionKwh ?? annualProduction);

                                                        return (
                                                            <tr key={q.id} className={contractAttached ? 'obd-quotes-table__row--hl' : ''}>
                                                                <td><span className="obd-quotes-table__product">{item.partner} · {item.product}</span></td>
                                                                <td>#{q.id}</td>
                                                                <td> 
                                                                    <span className="obd-quote-card__val">{q.fnanceMethod??'—'}</span>
                                                                </td>
                                                                <td>
                                                                    {q.partner !== 'Cash' && (
                                                                       
                                                                    <span className="obd-quote-card__val obd-quote-card__val--cost">
                                                                        ${q.monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} '/mo' 
                                                                    </span>
                                                                    )}
                                                                    {q.partner === 'Cash' && (
                                                                        <span className="obd-quote-card__val obd-quote-card__val--cost">
                                                                            ${q.epc?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {q.product.includes("Solar")  ? (
                                                                        <span className="obd-quote-card__row">
                                                                            ${q.solarRate?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                                                        </span>
                                                                    ) : q.product.includes("Roof") ? (
                                                                        <span className="obd-quote-card__row">
                                                                            {q.Roof_Square_Override?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sq.
                                                                        </span>
                                                                    ) :'—'}
                                                                </td>
                                                                <td>
                                                                    {q.product.includes("Solar") ? (
                                                                        <span className="obd-quote-card__row">
                                                                            ${q.grossPpw?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </span>
                                                                    ): q.product.includes("Roof") ? (
                                                                        <span className="obd-quote-card__row">
                                                                            ${q.Price_Per_Sqft?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </span>
                                                                    ):'—'}

                                                                </td>
                                                                <td>
                                                                    {q.partner != 'Cash' ? (
                                                                        <span className="obd-quote-card__row">
                                                                            ${q.epc?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </span>
                                                                    ):'—'}
                                                                </td>
                                                                <td>{typeof sysKw === 'number' ? sysKw.toFixed(2) : sysKw} kW</td>
                                                                <td>{prodKwh.toLocaleString()} kWh</td>
                                                                <td>
                                                                    {contractAttached ? (
                                                                        <span className={`obd-app-tag ${q.applicationStatus === 'Cancelled' ? 'obd-app-tag--cancelled' : 'obd-app-tag--submitted'}`}>
                                                                            {q.applicationId
                                                                                ? `${q.applicationStatus || 'Submitted'}`
                                                                                : 'Moved to contract'}
                                                                        </span>
                                                                    ) : (
                                                                        '—'
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="obd-quote-row__footer-actions" style={{ display: 'flex', gap: 8, marginTop: 0, paddingTop: 0, border: 'none', justifyContent: 'center' }}>
                                                                        <button type="button" 
                                                                            className="obd-quote-action obd-quote-action--icon" 
                                                                            onClick={() => setDetailsQuote({ quoteId: q.id, partner: q.partner, product: q.product, monthly: q.monthly, apr: q.apr })} 
                                                                            title="View Quote Details">
                                                                            <Eye size={18} weight="duotone" />
                                                                        </button>
                                                                        {((q.product === 'Roof' || q.product === 'Roof Only' || q.product === 'RO' || q.product === 'R') && q.product !== 'RR') && (
                                                                            <button
                                                                                type="button"
                                                                                className="obd-quote-action obd-quote-action--icon"
                                                                                onClick={() => handleGenerateProposal(q)}
                                                                                title="Generate Proposal"
                                                                                aria-label="Generate Proposal"
                                                                            >
                                                                                <FileText size={18} weight="duotone" />
                                                                            </button>
                                                                        )}
                                                                        {glCoEligible ? (
                                                                            <button
                                                                                type="button"
                                                                                className="obd-quote-action obd-quote-action--primary"
                                                                                style={{ fontSize: 11, padding: '4px 8px', height: 'auto', lineHeight: 1.25 }}
                                                                                disabled={
                                                                                    contractActionKey === `contract-${q.id}` ||
                                                                                    contractActionKey === `gl-co-${q.id}`
                                                                                }
                                                                                onClick={() => void submitGoodLeapUpdateChangeOrderFromQuotes(q)}
                                                                                title="Change Order"
                                                                            >
                                                                                {q.isSelectedCO ? 'Selected for Change Order' : 'Change Order'}
                                                                            </button>
                                                                        ) : contractAttached ? (
                                                                            <span
                                                                                className="obd-quote-action obd-quote-action--icon obd-quote-action--moved"
                                                                                title="Moved to contract"
                                                                                role="img"
                                                                                aria-label="Moved to contract"
                                                                            >
                                                                                <SealCheck size={18} weight="duotone" />
                                                                            </span>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="obd-quote-action obd-quote-action--icon obd-quote-action--primary"
                                                                                disabled={contractActionKey === `contract-${q.id}`}
                                                                                onClick={() => confirmAttachContractToQuote(q, item)}
                                                                                title={primaryLabel}
                                                                                aria-label={primaryLabel}
                                                                            >
                                                                                <SealCheck size={18} weight="duotone" />
                                                                            </button>
                                                                        )}
                                                                        {!isGenerateQuoteDisabled && item.product == "Solar" && (
                                                                            <button type="button" className="obd-quote-action obd-quote-action--icon" onClick={() => openAddQuoteModal(item, q)} title="Clone quote" aria-label="Add quote">
                                                                                <Plus size={18} weight="bold" /> 
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        paginatedRecords.map((c) => {
                                            const item = c.item;
                                            const partnerProductTitle = `${formatPartnerDisplayName(item.partner)} • ${item.product}`;
                                            if (c.type === 'initial') {
                                                const hasContract = !!initialContracts[item.key];
                                                const initialApp = initialContracts[item.key];
                                                return (
                                                    <div key={`initial-${item.key}`} className={`obd-quote-card obd-quote-card--scenario-style ${hasContract ? 'obd-quote-card--hl' : ''}`}>
                                                        <div className="obd-quote-card__head">
                                                            <span className="obd-quote-card__label">{partnerProductTitle} </span>
                                                        </div>
                                                        <div className="obd-quote-card__body">
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">
                                                                    {item.isCash ? 'EPC' : 'Monthly'}
                                                                </span>
                                                                <span className="obd-quote-card__val obd-quote-card__val--cost">
                                                                    ${item.isCash ? item.monthly.toLocaleString() : item.monthly.toFixed(2)}
                                                                    {!item.isCash ? '/mo' : ''}
                                                                </span>
                                                            </div>
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">System size</span>
                                                                <span className="obd-quote-card__val">{systemSizeKw} kW</span>
                                                            </div>
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Prod</span>
                                                                <span className="obd-quote-card__val">{annualProduction.toLocaleString()} kWh</span>
                                                            </div>
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Quote expires</span>
                                                                <span className="obd-quote-card__val">—</span>
                                                            </div>
                                                            {/* {initialApp && (
                                                                <div className="obd-quote-card__row obd-quote-card__row--tag">
                                                                    <span className={`obd-app-tag ${initialApp.status === 'Cancelled' ? 'obd-app-tag--cancelled' : 'obd-app-tag--submitted'}`}>
                                                                        {initialApp.applicationId} · {initialApp.status} 
                                                                    </span>
                                                                </div>
                                                            )} */}
                                                        </div>
                                                        <div className="obd-quote-card__footer">
                                                            <div className="obd-quote-card__footer-group" role="group" aria-label="Quote actions">
                                                                <button type="button" className="obd-quote-card__footer-btn" 
                                                                    onClick={() => setDetailsQuote({ partner: item.partner, product: item.product, monthly: item.monthly, apr: item.apr })} 
                                                                    title="View Quote Details">
                                                                    <Eye size={16} weight="duotone" />
                                                                </button>
                                                                {!hasContract && (
                                                                    <div className="obd-quote-card__footer-group-main">
                                                                        <button
                                                                            type="button"
                                                                            className="obd-quote-card__footer-btn"
                                                                            onClick={() =>
                                                                                noticeApi.info({
                                                                                    message: 'Generate a quote first',
                                                                                    description:
                                                                                        'Use “Add quote” and submit a saved quote before creating a contract.',
                                                                                    className: notifClass,
                                                                                })
                                                                            }
                                                                            title="New contract"
                                                                        >
                                                                            <SealCheck size={16} weight="duotone" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {!isGenerateQuoteDisabled && (
                                                                    <button type="button" className="obd-quote-card__footer-btn" onClick={() => openAddQuoteModal(item, q)} title="Add quote">
                                                                        <Plus size={16} weight="bold" /> 
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            const q = c.quote!;
                                            const contractAttached = isQuoteMovedToContract(q);
                                            const onPrimaryAction = () => {
                                                if (contractAttached) return;
                                                confirmAttachContractToQuote(q, item);
                                            };
                                            const sysKw = (q.systemSizeKw ?? systemSizeKw);
                                            const prodKwh = (q.productionKwh ?? annualProduction);
                                            const expiryStr = q.quoteExpiresAt ?? null;
                                            const createdStr =  q.quoteCreatedStr ?? null;
                                            const glCoEligible = goodLeapQuoteChangeOrderUiEligible(q);
                                            return (
                                                <div key={q.id} className={`obd-quote-card obd-quote-card--scenario-style 
                                                ${contractAttached ? 'obd-quote-card--hl' : ''}
                                                ${contractAttached && q.applicationId ?'contract-signed-quote' :''}`}>
                                                    <div className={`obd-quote-card__head`}>
                                                        <span className="obd-quote-card__label obd-quote-card__label--with-quote-id">
                                                            <span className="obd-quote-card__label-copy">{partnerProductTitle} </span>
                                                            <span className="obd-quote-card__label-quote-id">#{q.id}</span>
                                                        </span>
                                                        {(q.SunnovaQuote === 'Awaiting Sunnova Quote Sync' && item.partner.toLocaleLowerCase() === "igs") && (
                                                            <button
                                                                type="button"
                                                                className="obd-quote-card__error"
                                                                key="quote-issue"
                                                                title="View error details"
                                                                aria-label="View error details"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSunnovaErrorData(q.ErrorMessage ?? null);
                                                                    setSunnovaErrorOpen(true);
                                                                }}
                                                            >
                                                                !
                                                            </button>
                                                        )}
                                                        {(item.partner.toLocaleLowerCase() === COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase() &&
                                                            q.Finance_Partner_Contract_Status) && (
                                                            <span
                                                                className={contractStatusPillClass(
                                                                    String(q.Finance_Partner_Contract_Status),
                                                                )}
                                                            >
                                                                {q.Finance_Partner_Contract_Status}
                                                            </span>
                                                        )}
                                                        
                                                    </div>
                                                    <div className="obd-quote-card__body">
                                                        <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">Finance Method</span>
                                                            <span className="obd-quote-card__val">{q.fnanceMethod??'-'}</span>
                                                        </div>
                                                        {q.partner !== 'Cash' && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Monthly </span>
                                                                <span className="obd-quote-card__val obd-quote-card__val--cost">${q.monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{q.pricingMethod !== 'Cash' ? '/mo' : ''}</span>
                                                            </div>
                                                            )
                                                        }
                                                        {q.partner == 'Cash' && (
                                                            <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">EPC </span>
                                                            <span className="obd-quote-card__val obd-quote-card__val--cost">${q.epc?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                        </div>
                                                        )}
                                                        {q.product.includes("Solar")  && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Solar Rate</span>
                                                                <span className="obd-quote-card__val">${q.solarRate?.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
                                                            </div>
                                                        )}
                                                        {q.product.includes("Solar") && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">
                                                                {q.partner !== 'Cash' ? 'Gross PPW' : 'Desired PPW'}
                                                                </span>
                                                                <span className="obd-quote-card__val">${q.grossPpw?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        )}

                                                        {q.product.includes("Roof") && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Roof Squares</span>
                                                                <span className="obd-quote-card__val">{q.Roof_Square_Override?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sq.</span>
                                                            </div>
                                                        )}

                                                        {q.product.includes("Roof") && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Price/Sqft</span>
                                                                <span className="obd-quote-card__val">${q.Price_Per_Sqft?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        )}

                                                        {q.partner != 'Cash' && (
                                                            <div className="obd-quote-card__row">
                                                                <span className="obd-quote-card__key">Total EPC(Net)</span>
                                                                <span className="obd-quote-card__val">${q.epc?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        )}

                                                        <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">System size</span>
                                                            <span className="obd-quote-card__val">{typeof sysKw === 'number' ? sysKw.toFixed(2) : sysKw} kW</span>
                                                        </div>
                                                        <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">Prod</span>
                                                            <span className="obd-quote-card__val">{prodKwh.toLocaleString()} kWh</span>
                                                        </div>
                                                        <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">Quote expires</span>
                                                            <span className="obd-quote-card__val">{expiryStr ?? '—'}</span>
                                                        </div>
                                                        <div className="obd-quote-card__row">
                                                            <span className="obd-quote-card__key">Quote Created</span>
                                                            <span className="obd-quote-card__val">{createdStr ?? '—'}</span>
                                                        </div>
                                                        {contractAttached && !q.applicationId && (
                                                            <div className="obd-quote-card__row obd-quote-card__row--tag">
                                                                <span className={`obd-app-tag ${q.applicationStatus === 'Cancelled' ? 'obd-app-tag--cancelled' : 'obd-app-tag--submitted'}`}>
                                                                    {
                                                                    // q.applicationId
                                                                    //     ? `${q.applicationId} · ${q.applicationStatus || 'Submitted'}`
                                                                    //     : 
                                                                        'Moved to contract'
                                                                    } 
                                                                </span>
                                                            </div>
                                                        )}
                                                        {q.clonedQuoteId && (
                                                            <div className="obd-quote-card__row obd-quote-card__row--tag">
                                                                <span className="obd-app-tag obd-app-tag--cloned">Cloned from the Quote #{q.clonedQuoteId}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="obd-quote-card__footer">
                                                        <div className="obd-quote-card__footer-group" role="group" aria-label="Quote actions">
                                                            <button type="button" 
                                                                className="obd-quote-card__footer-btn" 
                                                                onClick={() => setDetailsQuote({ quoteId: q.id, partner: q.partner, product: q.product, monthly: q.monthly, apr: q.apr })} 
                                                                title="View Quote Details">
                                                                <Eye size={16} weight="duotone" />
                                                            </button>
                                                            {((q.product === 'Roof' || q.product === 'Roof Only' || q.product === 'RO' || q.product === 'R') && q.product !== 'RR') && (
                                                                <button
                                                                    type="button"
                                                                    className="obd-quote-card__footer-btn"
                                                                    onClick={() => handleGenerateProposal(q)}
                                                                    title="Generate Proposal"
                                                                >
                                                                    <FileText size={16} weight="duotone" />
                                                                </button>
                                                            )}
                                                            <div className="obd-quote-card__footer-group-main">
                                                                {glCoEligible ? (
                                                                    <button
                                                                        type="button"
                                                                        className="obd-quote-card__footer-btn obd-quote-card__footer-btn--primary"
                                                                        disabled={
                                                                            contractActionKey === `contract-${q.id}` ||
                                                                            contractActionKey === `gl-co-${q.id}`
                                                                        }
                                                                        onClick={() => void submitGoodLeapUpdateChangeOrderFromQuotes(q)}
                                                                        title="Change Order"
                                                                    >
                                                                        {q.isSelectedCO ? 'Selected for Change Order' : 'Change Order'}
                                                                    </button>
                                                                ) : contractAttached ? (
                                                                    <span
                                                                        className="obd-quote-card__footer-btn obd-quote-card__footer-btn--moved"
                                                                        title="Moved to contract"
                                                                        role="img"
                                                                        aria-label="Moved to contract"
                                                                    >
                                                                        <SealCheck size={16} weight="duotone" />
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="obd-quote-card__footer-btn obd-quote-card__footer-btn--primary obd-quote-card__footer-btn--contract-pending"
                                                                        disabled={contractActionKey === `contract-${q.id}`}
                                                                        onClick={onPrimaryAction}
                                                                        title="Contract"
                                                                    >
                                                                        <SealCheck size={16} weight="duotone" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {!isGenerateQuoteDisabled && item.product == 'Solar' && (
                                                                <button type="button" className="obd-quote-card__footer-btn" onClick={() => openAddQuoteModal(item, q)} title="Clone quote">
                                                                    <Plus size={16} weight="bold" /> 
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                {totalRecords > 0 && (
                                    <div className="obd-quotes-pagination">
                                        <Pagination
                                            size="small"
                                            current={quoteCurrentPage}
                                            pageSize={QUOTES_PAGE_SIZE}
                                            total={totalRecords}
                                            showSizeChanger={false}
                                            onChange={(page) => {
                                                setQuoteCurrentPage(page);
                                            }}
                                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
    );

    const [confirmQuoteCellKey, setConfirmQuoteCellKey] = useState<selectedQuote | null>(null);
    const updateQuoteVersion = () => {
        setConfirmQuoteCellKey(null);
        setQuotesRefreshTrigger(Date.now());
        setAgreementsRefreshTrigger(Date.now());
        if (!quotesModalOwnsLoader) getQuotes();
    };

    const quotesLoadingMask = (
        <div
            className="obd-dashboard-modal__loading-overlay"
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="obd-dashboard-modal__loading-inner">
                <Spin size="default" />
                <span className="obd-dashboard-modal__loading-text">Getting quotes...</span>
            </div>
        </div>
    );

    return (
        <>
            {noticeContextHolder}
            {isModalLayout ? (
                <div
                    className={`obd-quotes-modal__frame${
                        embeddedInDashboardSection ? ' obd-quotes-modal__frame--dashboard-section' : ''
                    }`}
                >
                    <div className={`obd-quotes-loading-stack ${quotesDataLoading ? 'obd-quotes-loading-stack--active' : ''}`}>
                        {quotesToolbar}
                        {quotesMainBody}
                        {quotesDataLoading && quotesLoadingMask}
                    </div>
                    {toolsInFrameFooter && quoteToolsGroup ? (
                        <div className="obd-quotes-modal__frame-footer">{quoteToolsGroup}</div>
                    ) : null}
                </div>
            ) : (
                <div className="obd-card obd-card--quotes">
                    <button type="button" className="obd-quotes__header" onClick={() => setSectionOpen((o) => !o)}>
                        <span className="obd-card__label obd-card__label--section-icon">
                            <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                                <FileText size={16} weight="duotone" />
                            </span>
                            Quotes
                            <span className="obd-financing__chip">
                                {items.length} selected{totalQuotes > 0 ? ` · ${totalQuotes} quote${totalQuotes > 1 ? 's' : ''}` : ''}
                            </span>
                        </span>
                        <CaretDown size={14} weight="bold" className={`obd-quotes__chevron ${sectionOpen ? 'obd-quotes__chevron--open' : ''}`} />
                    </button>
                    {showQuotesBody && (
                        <div className={`obd-quotes-loading-stack ${quotesDataLoading ? 'obd-quotes-loading-stack--active' : ''}`}>
                            {quotesMainBody}
                            {quotesDataLoading && quotesLoadingMask}
                        </div>
                    )}
                </div>
            )}

            {detailsQuote && (
                <OBDQuoteDetailsModal
                    quoteId={detailsQuote.quoteId}
                    partner={detailsQuote.partner}
                    product={detailsQuote.product}
                    monthly={detailsQuote.monthly}
                    apr={detailsQuote.apr}
                    mode="view"
                    onClose={() => setDetailsQuote(null)}
                    opportunityId={opportunityIdForQuoteModal}
                    salesforceId={salesforceIdForQuoteModal}
                    opportunityDetails={opportunityDetails}
                    onAfterAction={async () => {
                        setQuotesRefreshTrigger(Date.now());
                    }}
                />
            )}

            {proposalModalOpen && proposalRow && (
                <ViewTrinityContractModal
                    open={proposalModalOpen}
                    onClose={() => {
                        setProposalModalOpen(false);
                        setProposalRow(null);
                    }}
                    row={proposalRow}
                    mode="proposal"
                    isGenerate={true}
                    theme={theme}
                    salesforceId={salesforceIdForQuoteModal}
                    opportunityId={opportunityIdForQuoteModal}
                    opportunityDetails={opportunityDetails}
                    onAfterAction={async () => {
                        setQuotesRefreshTrigger(Date.now());
                    }}
                />
            )}

            {addQuoteForm && (
                <OBDAddQuoteModal
                    formState={addQuoteForm}
                    liveMonthly={liveMonthly}
                    onUpdate={updateAddQuoteForm}
                    onSubmit={submitAddQuoteForm}
                    onClose={() => setAddQuoteForm(null)}
                    hasExistingAppSameMethod={(() => {
                        const item = items.find((i) => i.key === addQuoteForm.sourceKey);
                        return item ? hasActiveContractForMethod(item, addQuoteForm.pricingMethod) : false;
                    })()}
                />
            )}

            {contractConfirm && (
                <DashboardModal
                    title="Create contract"
                    open
                    onClose={() => {
                        if (!contractActionKey) setContractConfirm(null);
                    }}
                    zIndex={OBD_QUOTES_CONTRACT_CONFIRM_Z}
                    width={560}
                    theme={theme}
                    maskClosable={false}
                    className={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    footer={
                        <>
                            <Button onClick={() => setContractConfirm(null)} disabled={!!contractActionKey}>
                                No
                            </Button>
                            <Button type="primary" onClick={confirmAttachContract} loading={!!contractActionKey}>
                                Yes
                            </Button>
                        </>
                    }
                >
                    <div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <SealCheck size={20} weight="regular" />
                            <span>Do you want to create a contract for this quote?</span>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 13, opacity: 0.88 }}>
                            Quote <strong>#{contractConfirm.quote.id}</strong> ·{' '}
                            {formatPartnerDisplayName(contractConfirm.item.partner)} · {contractConfirm.item.product}
                        </div>
                    </div>
                </DashboardModal>
            )}

            
            <DashboardModal
                open={addQuoteProductPickerOpen}
                onClose={() => setAddQuoteProductPickerOpen(false)}
                title="Products for this quote"
                width={440}
                theme={theme}
                zIndex={10075}
                maskClosable
                footer={
                    <>
                        <Button onClick={() => setAddQuoteProductPickerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={confirmToolbarAddQuoteProductSelection}>
                            Continue
                        </Button>
                    </>
                }
            >
                <div style={{ padding: '4px 4px 8px', fontSize: 13, lineHeight: 1.45, color: theme === 'light' ? '#475569' : 'rgba(148,163,184,0.95)' }}>
                    Select which active opportunity products to include. The generate-quote form will only show sections for your selection.
                </div>
                {(addQuoteOpportunityProductRows.solar ||
                    addQuoteOpportunityProductRows.battery ||
                    addQuoteOpportunityProductRows.roof) && (
                    <div
                        style={{
                            marginBottom: 10,
                            padding: '10px 12px',
                            borderRadius: 8,
                            fontSize: 12,
                            lineHeight: 1.5,
                            background: theme === 'light' ? '#f1f5f9' : 'rgba(30,41,59,0.55)',
                            border: theme === 'light' ? '1px solid #e2e8f0' : '1px solid rgba(71,85,105,0.5)',
                            color: theme === 'light' ? '#334155' : 'rgba(203,213,225,0.95)',
                        }}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>From Products (Commerce Hub)</div>
                        {addQuoteOpportunityProductRows.solar && (
                            <div>
                                <strong>Solar</strong> — Finance partner:{' '}
                                {formatPartnerDisplayName(partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.solar)) ||
                                    '—'}
                                {' · '}
                                Method: {financeMethodNameFromOpportunityProduct(addQuoteOpportunityProductRows.solar) || '—'}
                            </div>
                        )}
                        {addQuoteOpportunityProductRows.battery && (
                            <div style={{ marginTop: 4 }}>
                                <strong>Battery</strong> — Finance partner:{' '}
                                {formatPartnerDisplayName(partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.battery)) ||
                                    '—'}
                                {' · '}
                                Method:{' '}
                                {partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.battery).toLowerCase() ===
                                'cash'
                                    ? '—'
                                    : financeMethodNameFromOpportunityProduct(addQuoteOpportunityProductRows.battery) || '—'}
                            </div>
                        )}
                        {addQuoteOpportunityProductRows.roof && (
                            <div style={{ marginTop: 4 }}>
                                <strong>Roof</strong> — Finance partner:{' '}
                                {formatPartnerDisplayName(partnerDisplayNameFromOpportunityProduct(addQuoteOpportunityProductRows.roof)) ||
                                    '—'}
                                {' · '}
                                Method: {financeMethodNameFromOpportunityProduct(addQuoteOpportunityProductRows.roof) || '—'}
                            </div>
                        )}
                    </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 4px 12px' }}>
                    {addQuoteProductsOnOpportunity.solar && (
                        <Checkbox 
                            checked={pickSolarForAddQuote} 
                            onChange={handlePickSolarForAddQuoteChange} 
                            disabled={ (SBLightReach || SBGoodleapSingleQuote) && ! addQuoteProductsOnOpportunity.roof}>
                            Solar
                        </Checkbox>
                    )}
                    {addQuoteProductsOnOpportunity.battery && (
                        <div>
                            <Checkbox
                                checked={pickBatteryForAddQuote}
                                disabled={!batteryAddQuoteCheckboxEnabled || SBLightReach  || SBGoodleapSingleQuote }
                                onChange={handlePickBatteryForAddQuoteChange} >
                                Battery
                            </Checkbox>
                            {!batteryAddQuoteCheckboxEnabled && batteryRequiresSolarForAddQuote && !pickSolarForAddQuote && (
                                <div
                                    style={{
                                        marginTop: 2,
                                        marginLeft: 24,
                                        fontSize: 12,
                                        lineHeight: 1.4,
                                        color: theme === 'light' ? '#64748b' : 'rgba(148,163,184,0.9)',
                                    }}
                                >
                                    Include Solar in this quote to use Battery (same finance partner on both products).
                                </div>
                            )}
                        </div>
                    )}
                    {addQuoteProductsOnOpportunity.roof && (
                        <Checkbox checked={pickRoofForAddQuote} onChange={handlePickRoofForAddQuoteChange}>
                            Roof
                        </Checkbox>
                    )}
                </div>
            </DashboardModal>

            <GenerateQuoteModal
                confirmQuoteCellKey={confirmQuoteCellKey}
                priceQuote={priceQuote}
                setConfirmQuoteCellKey={setConfirmQuoteCellKey}
                updateQuoteVersion={updateQuoteVersion} 
                quoteForm={quoteForm} 
                setQuoteForm={setQuoteForm}            
                noticeApi={noticeApi}
            />

            <DashboardModal
                title="Eligible Products"
                open={lightreachEligibleOpen}
                onClose={() => setLightreachEligibleOpen(false)}
                footer={null}
                width="min(1200px, 98vw)"
                theme={theme}
                maskClosable={false}
                destroyOnClose
                className="obd-dashboard-modal--lr-eligible-products"
                rootClassName={`obd-theme-${theme}`}
                zIndex={10070}
            >
                <div className="obd-lr-eligible-products-table">
                    <AntTable<Record<string, unknown>>
                        size="middle"
                        loading={lightreachEligibleLoading}
                        columns={LIGHTREACH_ELIGIBLE_COLUMNS}
                        dataSource={lightreachEligibleRows}
                        rowKey={(r, i) =>
                            String(r.ProductId ?? r.Id ?? r.Offer_Id ?? r.offer_Id ?? `lr-elig-${i}`)
                        }
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) =>
                                total > 0 ? `${range[0]}–${range[1]} of ${total}` : '',
                            className: 'obd-lr-eligible-products-pagination',
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </DashboardModal>

            <LightreachBatteryCapModal
                open={lightreachBatteryCapOpen}
                onClose={() => setLightreachBatteryCapOpen(false)}
                theme={theme}
                leadId={getLightreachLeadIdFromOpportunity(opportunityDetails)}
            />

            <DashboardModal
                title="Roof / Battery Calculator"
                open={roofingCalcOpen}
                onClose={() => setRoofingCalcOpen(false)}
                footer={null}
                width="min(900px, 92vw)"
                theme={theme}
                centered={false}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-sync-modal-root obd-form-controls-modal-root obd-contact-modal obd-roofing-calculator-dashboard-modal obd-theme-${theme}`}
                zIndex={10070}
                modalStyles={{ content: { marginTop: 24 } }}
            >
                <RoofingCalculatorModal
                    priceQuote={priceQuote}
                    opportunityDetails={opportunityDetails}
                    onRequestClose={() => setRoofingCalcOpen(false)}
                />
            </DashboardModal>

            <DashboardModal
                title="Add Adders"
                open={addersOpen}
                onClose={() => setAddersOpen(false)}
                footer={
                    <>
                        <Button onClick={() => setAddersOpen(false)} disabled={addersSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={handleSaveAdders} loading={addersSaving}>
                            Save
                        </Button>
                    </>
                }
                width="80vw"
                theme={theme}
                centered
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-sync-modal-root obd-form-controls-modal-root obd-contact-modal obd-adders-dashboard-modal obd-theme-${theme}`}
                zIndex={10070}
                loading={addersLoading}
                loadingMessage="Loading adders..."
            >
                <AddersModal
                    ref={addersModalRef}
                    productTypes={availableAdderProductTypes}
                    theme={theme}
                    opportunityId={opportunityIdForQuoteModal}
                    opportunityDetails={opportunityDetails}
                    opportunityProductAdders={opportunityProductAdders}
                />
            </DashboardModal>

            <IGSCapsModal
                open={igsCapsOpen}
                onClose={() => setIgsCapsOpen(false)}
                opportunityId={opportunityIdForQuoteModal}
                theme={theme}
            />

            <IGSEpcCalcModal
                open={igsEpcCalcOpen}
                onClose={() => setIgsEpcCalcOpen(false)}
                theme={theme}
                quoteDetails={priceQuote}
                financeMethodId={
                    (() => {
                        // Angular: `this.solar?.contract_Type` (for IGS Solar) usually maps to Finance_Method_Name like "PPA"
                        const fromQuote = (priceQuote?.Solar?.Finance_Method_Name ?? priceQuote?.Solar?.contract_Type ?? priceQuote?.Solar?.ContractType) as any;
                        if (fromQuote != null && String(fromQuote).trim() !== '') return String(fromQuote).trim();
                        const products = opportunityDetails?.OpportunityProduct || [];
                        const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
                        return String(
                            solar?.Finance_Method_Name ??
                            solar?.finance_Method_Name ??
                            solar?.contract_Type ??
                            solar?.Contract_Type ??
                            solar?.ContractType ??
                            solar?.Finance_Method ??
                            ''
                        ).trim() || null;
                    })()
                }
                partnerDisplayName="IGS"
                opportunityId={opportunityIdForQuoteModal}
            />

            <SunnovaSystemErrorModal
                open={sunnovaErrorOpen}
                onClose={() => {
                    setSunnovaErrorOpen(false);
                    setSunnovaErrorData(null);
                }}
                data={sunnovaErrorData}
                theme={theme}
                zIndex={11075}
            />
        </>
    );
};

export default QuotesContractsCard;
